import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime, formatWallTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import {
  tenantApi,
  Schedule,
  Group,
  User,
  GroupMemberUser,
  ScheduleWindow,
  RotationConfig,
  ScheduleShiftSwap,
  ScheduleTimelineEntry,
  ScheduleGap,
} from '../../services/tenantApi';
import { ScheduleLinearTimeline, type LinearTimelineEntry } from '../../user-dashboard/ScheduleLinearTimeline';
import { OVERRIDE_SEGMENT_COLOR, timelineSourceBackground } from '../../user-dashboard/timelineSources';
import { getLinkedEscalation } from '../../src/utils/scheduleLinking';
import {
  localToUtc,
  parseDateTimeLocalInTimezone,
  roundUpToMinute,
  toDateTimeLocalInTimezone,
  toLocalParts,
} from './scheduleTimezone';
import { RosterRemovalModal } from './RosterRemovalModal';
import type { RosterRemovalResult, ScheduleCover, ScheduleCoverCreateResult } from '../../services/tenantApi';
import { ScheduleCoverModal, type CoverModalInitial } from './ScheduleCoverModal';
import { ScheduleCoversPanel, describeCoverSegment } from './ScheduleCoversPanel';
import { useAppDialog } from './AppDialog';
import { SchedulerWizardHowTo } from './SchedulerWizardHowTo';
import { ScheduleReplacementHowTo, type ReplacementHelpTopic } from './ScheduleReplacementHowTo';
import { SchedulerWizard } from './SchedulerWizard';
import { OnCallPresencePanel } from './OnCallPresencePanel';

/** How far back the covers panel lists finished covers. */
const COVER_HISTORY_DAYS = 30;

type RosterRemovalTarget = { scheduleId: string; timezone: string; config: RotationConfig; userId: string; label: string };
type RosterAttentionFlag = 'inactive' | 'notInGroup';

const ROSTER_REMOVAL_DENIED_TOOLTIP = 'Only schedule managers or team leads can remove roster members';

const closeActionMenuOnEscape = (event: React.KeyboardEvent<HTMLDetailsElement>) => {
  if (event.key !== 'Escape') return;
  event.currentTarget.open = false;
  event.currentTarget.querySelector('summary')?.focus();
};

const closeActionMenuOnSelection = (event: React.MouseEvent<HTMLDivElement>) => {
  if ((event.target as HTMLElement).closest('button'))
    event.currentTarget.closest('details')?.removeAttribute('open');
};

/** Roster members that should be removed: inactive users, or users no longer in the rotation's group (when group membership is known). */
export function findRosterAttention(
  roster: Array<{ userId: string }>,
  users: Array<{ userId: string; status?: string }>,
  groupMembers?: Array<{ userId: string; status?: string }> | null,
): Map<string, RosterAttentionFlag> {
  const flags = new Map<string, RosterAttentionFlag>();
  const userStatus = new Map(users.map(u => [u.userId.toLowerCase(), (u.status || '').toLowerCase()]));
  const groupStatus = groupMembers ? new Map(groupMembers.map(m => [m.userId.toLowerCase(), (m.status || '').toLowerCase()])) : null;
  for (const entry of roster || []) {
    const key = String(entry.userId || '').toLowerCase();
    if (!key) continue;
    const status = groupStatus?.get(key) ?? userStatus.get(key);
    if (status && status !== 'active') flags.set(entry.userId, 'inactive');
    else if (groupStatus && !groupStatus.has(key)) flags.set(entry.userId, 'notInGroup');
  }
  return flags;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SCHEDULE_TYPES: Array<'Primary' | 'Escalation'> = ['Primary', 'Escalation'];
type RotationRuleEditor = {
  dayOfWeek: number;
  startTimeLocal: string;
  endTimeLocal: string;
  isOvernight: boolean;
  durationHours?: number;
  durationMinutes?: number;
};

type TimelineSwapSelection = {
  scheduleId: string;
  scheduleName: string;
  windowId: string;
  windowName: string;
  entry: LinearTimelineEntry;
  rotationGroupId?: string;
  rotationGroupName?: string;
};

type SwapModalContext = {
  scheduleId: string;
  source?: TimelineSwapSelection | null;
};

type SwapVisualState = 'pending' | 'approved' | 'applied' | 'rejected' | 'cancelled' | 'other';

const COMMON_TIMEZONES = [
  'UTC',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Pacific/Auckland',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
];

function formatDateTimeInTimezone(date: Date, timezone: string): string {
  if (Number.isNaN(date.getTime())) return '';

  return formatDateTime(date, {
    timeZone: timezone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function tryParseLockTargetRotationId(lockKey?: string): string | null {
  if (!lockKey) return null;
  const raw = String(lockKey).trim();
  if (!raw.toLowerCase().startsWith('rotation:')) return null;
  const value = raw.substring('rotation:'.length).trim();
  return value || null;
}

export const SchedulesManagement: React.FC<{ onOpenOverrides?: () => void }> = ({ onOpenOverrides }) => {
  const [replacementHelp, setReplacementHelp] = useState<ReplacementHelpTopic | null>(null);
  const timelinePanelRef = useRef<HTMLDivElement>(null);
  const openReplacementHelp = (topic: ReplacementHelpTopic) => {
    setReplacementHelp(topic);
    timelinePanelRef.current?.scrollIntoView?.({ block: 'start' });
  };
  const [showSchedulerHowTo, setShowSchedulerHowTo] = useState(false);
  const preferences = useDateTimePreferences();
  const browserTimezone = preferences.timezone;
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);
  const [rotationsByWindow, setRotationsByWindow] = useState<Record<string, RotationConfig[]>>({});
  const [timeline, setTimeline] = useState<ScheduleTimelineEntry[]>([]);
  const [gaps, setGaps] = useState<ScheduleGap[]>([]);
  const [swaps, setSwaps] = useState<ScheduleShiftSwap[]>([]);
  const [linkedEscalationScheduleId, setLinkedEscalationScheduleId] = useState<string>('');
  const [savedLinkedEscalationScheduleId, setSavedLinkedEscalationScheduleId] = useState<string>('');
  const [savingLinkedEscalation, setSavingLinkedEscalation] = useState(false);
  const [linkedEscalationSchedule, setLinkedEscalationSchedule] = useState<Schedule | null>(null);
  const [linkedWindows, setLinkedWindows] = useState<ScheduleWindow[]>([]);
  const [linkedRotationsByWindow, setLinkedRotationsByWindow] = useState<Record<string, RotationConfig[]>>({});
  const [linkedTimeline, setLinkedTimeline] = useState<ScheduleTimelineEntry[]>([]);
  const [onCallData, setOnCallData] = useState<{ allSchedules: { scheduleId: string; scheduleName: string; userName: string; userEmail: string; startTime: string; endTime: string }[] } | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [viewerTimezone, setViewerTimezone] = useState<string>(browserTimezone);
  useEffect(() => { setViewerTimezone(preferences.timezone); }, [preferences.timezone]);
  const [timelineSpanDays, setTimelineSpanDays] = useState<1 | 7 | 14 | 30>(7);
  const [calendarSpanDays] = useState<7 | 14 | 30>(30);
  const [showTimelineDebug, setShowTimelineDebug] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [showCreateScheduleModal, setShowCreateScheduleModal] = useState(false);
  const [showSchedulerWizard, setShowSchedulerWizard] = useState(false);
  const [showWindowModal, setShowWindowModal] = useState(false);
  const [windowTarget, setWindowTarget] = useState<ScheduleWindow | null>(null);
  const [showRotationModal, setShowRotationModal] = useState<{ windowId: string } | null>(null);
  const [editRotationTarget, setEditRotationTarget] = useState<{ windowId: string; config: RotationConfig } | null>(null);
  const [swapModalContext, setSwapModalContext] = useState<SwapModalContext | null>(null);
  const [selectedSwapSource, setSelectedSwapSource] = useState<TimelineSwapSelection | null>(null);
  const [rosterEditorTarget, setRosterEditorTarget] = useState<{ config: RotationConfig; window: ScheduleWindow } | null>(null);
  const [rosterGroupMembers, setRosterGroupMembers] = useState<GroupMemberUser[]>([]);
  const [editScheduleModal, setEditScheduleModal] = useState<Schedule | null>(null);
  const [canRemoveRosterMembers, setCanRemoveRosterMembers] = useState(false);
  const [rosterRemovalTarget, setRosterRemovalTarget] = useState<RosterRemovalTarget | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [covers, setCovers] = useState<ScheduleCover[]>([]);
  const [coverModal, setCoverModal] = useState<CoverModalInitial | null>(null);
  const [coverBusyId, setCoverBusyId] = useState<string | null>(null);
  const [canForceCover, setCanForceCover] = useState(false);
  const { alert: alertDialog, confirm: confirmDialog, dialog: appDialog } = useAppDialog();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    setActionError(null);
    try {
      const [scheduleData, groupData, userData, currentUser, onCall] = await Promise.all([
        tenantApi.getSchedules(),
        tenantApi.getGroups(),
        tenantApi.getUsers({ take: 1000 }),
        tenantApi.getCurrentUser().catch(() => null),
        tenantApi.getOnCallSchedule().catch(() => null),
      ]);
      const loadedSchedules = scheduleData || [];
      setSchedules(loadedSchedules);
      setGroups(groupData || []);
      setUsers(userData?.users || []);
      setOnCallData(onCall);
      const tz = currentUser?.timezone?.trim();
      if (tz) setViewerTimezone(tz);
      const roles = new Set<string>((currentUser?.roles || []).map((r: unknown) => String(r || '').toLowerCase()));
      const permissions = new Set<string>((currentUser?.permissions || []).map((p: unknown) => String(p || '').toLowerCase()));
      // The server enforces rotation-scoped team lead checks; this only decides whether to offer the action.
      setCanRemoveRosterMembers(
        roles.has('tenantadmin') || roles.has('tenant admin') || roles.has('teamlead') || roles.has('team lead') || permissions.has('manageschedules'),
      );
      setCanForceCover(roles.has('tenantadmin') || roles.has('tenant admin') || permissions.has('manageschedules'));
      if (loadedSchedules.length > 0) {
        await selectSchedule(loadedSchedules[0], loadedSchedules);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectSchedule = async (schedule: Schedule, availableSchedules: Schedule[] = schedules) => {
    setSelectedSchedule(schedule);
    const linkedEscalation = getLinkedEscalation(schedule, availableSchedules);
    const persistedLinkedEscalationId = linkedEscalation.mode === 'persisted' ? (linkedEscalation.linked?.scheduleId || '') : '';
    const initialLinkedEscalationId = linkedEscalation.linked?.scheduleId || '';
    setLinkedEscalationScheduleId(initialLinkedEscalationId);
    setSavedLinkedEscalationScheduleId(persistedLinkedEscalationId);
    setLinkedEscalationSchedule(null);
    setLinkedWindows([]);
    setLinkedRotationsByWindow({});
    setLinkedTimeline([]);
    setCovers([]);
    await Promise.allSettled([
      loadWindows(schedule.scheduleId),
      loadTimeline(schedule.scheduleId),
      loadGaps(schedule.scheduleId),
      loadSwaps(schedule.scheduleId),
      loadCovers(schedule.scheduleId),
    ]);

    if (initialLinkedEscalationId) {
      await loadLinkedEscalation(initialLinkedEscalationId, availableSchedules);
    }
  };

  const loadWindows = async (scheduleId: string) => {
    const ws = await tenantApi.getScheduleWindows(scheduleId);
    const orderedWindows = (ws || []).slice().sort((a, b) => {
      const byOrder = (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0);
      if (byOrder !== 0) return byOrder;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
    setWindows(orderedWindows);

    const map: Record<string, RotationConfig[]> = {};
    for (const w of orderedWindows) {
      try {
        const rotationConfigs = await tenantApi.getScheduleRotations(scheduleId, w.windowId);
        map[w.windowId] = rotationConfigs;
      } catch (err) {
        console.error(`Failed to load rotations for window ${w.windowId}`, err);
        map[w.windowId] = [];
      }
    }

    setRotationsByWindow(map);
  };

  const loadTimeline = async (scheduleId: string) => {
    const rangeStart = alignToLocalMidnightUtc(new Date(), viewportTz);
    const rangeEnd = new Date(rangeStart.getTime() + calendarSpanDays * 86400000);
    const res = await tenantApi.getScheduleTimeline(scheduleId, rangeStart.toISOString(), rangeEnd.toISOString());
    setTimeline(res.entries || []);

    if (linkedEscalationScheduleId) {
      const linkedRes = await tenantApi.getScheduleTimeline(linkedEscalationScheduleId, rangeStart.toISOString(), rangeEnd.toISOString());
      setLinkedTimeline(linkedRes.entries || []);
    }
  };

  const loadGaps = async (scheduleId: string) => {
    const rangeStart = alignToLocalMidnightUtc(new Date(), viewportTz);
    const rangeEnd = new Date(rangeStart.getTime() + calendarSpanDays * 86400000);
    const res = await tenantApi.getScheduleGaps(scheduleId, rangeStart.toISOString(), rangeEnd.toISOString());
    setGaps(res.gaps || []);
  };

  const loadSwaps = async (scheduleId: string) => {
    const data = await tenantApi.getScheduleSwaps(scheduleId);
    setSwaps(data || []);
  };

  const loadCovers = async (scheduleId: string) => {
    const from = new Date(Date.now() - COVER_HISTORY_DAYS * 86400000).toISOString();
    const data = await tenantApi.getScheduleCovers(scheduleId, { from });
    setCovers(data || []);
  };

  const refreshSelected = async () => {
    if (!selectedSchedule) return;
    await Promise.allSettled([
      loadWindows(selectedSchedule.scheduleId),
      loadTimeline(selectedSchedule.scheduleId),
      loadGaps(selectedSchedule.scheduleId),
      loadSwaps(selectedSchedule.scheduleId),
      loadCovers(selectedSchedule.scheduleId),
    ]);
  };

  const loadLinkedEscalation = async (escalationScheduleId: string, availableSchedules: Schedule[] = schedules) => {
    setLinkedEscalationScheduleId(escalationScheduleId || '');
    if (!escalationScheduleId) {
      setLinkedEscalationSchedule(null);
      setLinkedWindows([]);
      setLinkedRotationsByWindow({});
      setLinkedTimeline([]);
      return;
    }

    const escalation = availableSchedules.find(s => s.scheduleId === escalationScheduleId) || null;
    setLinkedEscalationSchedule(escalation);

    const ws = await tenantApi.getScheduleWindows(escalationScheduleId);
    const orderedWindows = (ws || []).slice().sort((a, b) => {
      const byOrder = (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0);
      if (byOrder !== 0) return byOrder;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
    setLinkedWindows(orderedWindows);

    const map: Record<string, RotationConfig[]> = {};
    for (const w of orderedWindows) {
      try {
        const rotationConfigs = await tenantApi.getScheduleRotations(escalationScheduleId, w.windowId);
        map[w.windowId] = rotationConfigs;
      } catch {
        map[w.windowId] = [];
      }
    }
    setLinkedRotationsByWindow(map);

    const rangeStart = alignToLocalMidnightUtc(new Date(), viewportTz);
    const rangeEnd = new Date(rangeStart.getTime() + calendarSpanDays * 86400000);
    const linkedRes = await tenantApi.getScheduleTimeline(escalationScheduleId, rangeStart.toISOString(), rangeEnd.toISOString());
    setLinkedTimeline(linkedRes.entries || []);
  };

  const saveLinkedEscalationLink = async () => {
    if (!selectedSchedule || selectedSchedule.scheduleType !== 'Primary') return;

    setSavingLinkedEscalation(true);
    setActionError(null);
    const primaryScheduleId = selectedSchedule.scheduleId;

    try {
      const escalationSchedules = schedules.filter(s => s.scheduleType === 'Escalation');
      const updates: Promise<any>[] = [];

      for (const escalation of escalationSchedules) {
        if (escalation.scheduleId === linkedEscalationScheduleId) {
          if (escalation.linkedPrimaryScheduleId !== primaryScheduleId) {
            updates.push(tenantApi.updateSchedule(escalation.scheduleId, { linkedPrimaryScheduleId: primaryScheduleId }));
          }
          continue;
        }

        if (escalation.linkedPrimaryScheduleId === primaryScheduleId) {
          updates.push(tenantApi.updateSchedule(escalation.scheduleId, { linkedPrimaryScheduleId: null }));
        }
      }

      if (updates.length > 0) {
        await Promise.all(updates);
      }

      setSchedules((prev) => prev.map((s) => {
        if (s.scheduleType !== 'Escalation') return s;
        if (s.scheduleId === linkedEscalationScheduleId) {
          return { ...s, linkedPrimaryScheduleId: primaryScheduleId };
        }
        if (s.linkedPrimaryScheduleId === primaryScheduleId) {
          return { ...s, linkedPrimaryScheduleId: undefined };
        }
        return s;
      }));

      setSavedLinkedEscalationScheduleId(linkedEscalationScheduleId);
      window.dispatchEvent(new CustomEvent('nog:schedules:changed'));
    } catch (err: any) {
      setActionError(err?.message || 'Failed to save linked escalation schedule');
    } finally {
      setSavingLinkedEscalation(false);
    }
  };

  const openRosterEditor = async (config: RotationConfig, window: ScheduleWindow) => {
    try {
      const members = await tenantApi.getGroupMembers(config.rotation.groupId);
      setRosterGroupMembers(members || []);
    } catch (err) {
      console.error('Failed to load group members for roster editor:', err);
      setRosterGroupMembers([]);
    }
    setRosterEditorTarget({ config, window });
  };

  const openRosterRemoval = (config: RotationConfig, userId: string, label: string) => {
    if (!selectedSchedule) return;
    setActionNotice(null);
    setRosterRemovalTarget({ scheduleId: selectedSchedule.scheduleId, timezone: selectedSchedule.timezone || 'UTC', config, userId, label });
  };

  const userLabel = (userId: string | null | undefined) => {
    if (!userId) return '—';
    const key = userId.toLowerCase();
    const u = users.find(x => x.userId.toLowerCase() === key);
    if (u) return u.displayName || u.email || userId;
    const rosterEntry = Object.values(rotationsByWindow).flat().flatMap(rc => rc.roster || []).find(r => r.userId.toLowerCase() === key);
    return rosterEntry?.userName || rosterEntry?.userEmail || userId;
  };

  const onRosterMemberRemoved = async (target: RosterRemovalTarget, result: RosterRemovalResult) => {
    setRosterRemovalTarget(null);
    setRosterEditorTarget(null);
    setRosterGroupMembers([]);
    const immediate = Date.parse(result.effectiveFromUtc) <= Date.now() + 60_000;
    const parts = [`${target.label} was removed from ${target.config.rotation.name}`];
    parts[0] += immediate ? ' effective now.' : ` from ${fmtSchedule(result.effectiveFromUtc)}.`;
    if (immediate && result.handoverUserId) parts.push(`${userLabel(result.handoverUserId)} is now on call.`);
    if (result.remainingMembers === 0) parts.push('The rotation has no members left – assign cover to avoid a coverage gap.');
    const swapCount = (result.cancelledSwapIds?.length || 0) + (result.revertedSwapIds?.length || 0);
    if (swapCount > 0) parts.push(`${swapCount} affected swap${swapCount === 1 ? '' : 's'} cancelled.`);
    setActionNotice(parts.join(' '));
    globalThis.window?.dispatchEvent(new CustomEvent('nog:schedules:changed'));
    await refreshSelected();
  };

  const coverRotations = useMemo(
    () => Object.values(rotationsByWindow).flat().map(rc => ({ rotationId: rc.rotation.rotationId, name: rc.rotation.name, groupId: rc.rotation.groupId })),
    [rotationsByWindow],
  );
  const coverableUsers = useMemo(() => {
    const seen = new Map<string, string>();
    Object.values(rotationsByWindow).flat().flatMap(rc => rc.roster || []).forEach(r => {
      const key = r.userId.toLowerCase();
      if (!seen.has(key)) seen.set(key, r.userId);
    });
    return Array.from(seen.values())
      .map(userId => ({ userId, label: userLabel(userId) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rotationsByWindow, users]);
  const coverTenantUsers = useMemo(
    () => users.map(u => ({ userId: u.userId, label: u.displayName || u.email || u.userId, status: u.status })),
    [users],
  );
  const coversById = useMemo(() => new Map(covers.map(c => [c.coverId.toLowerCase(), c])), [covers]);

  const openCoverModal = (initial: CoverModalInitial = {}) => {
    if (!selectedSchedule) return;
    setActionNotice(null);
    setCoverModal(initial);
  };

  const onCoverCreated = async (result: ScheduleCoverCreateResult) => {
    setCoverModal(null);
    const cover = result.cover;
    const parts = [
      `${cover.replacementUserName || userLabel(cover.replacementUserId)} is covering ${cover.coveredUserName || userLabel(cover.coveredUserId)} from ${fmtSchedule(cover.startUtc)} to ${fmtSchedule(cover.endUtc)}.`,
    ];
    if (result.forced) parts.push('The replacement is not in the rotation group (forced and audited).');
    if (result.replacementAlreadyOnCall) {
      const where = Array.from(new Set((result.replacementConflicts || []).map(c => c.rotationName).filter(Boolean))).join(', ');
      parts.push(`Warning: the replacement is already on call at overlapping times${where ? ` in ${where}` : ''}.`);
    }
    setActionNotice(parts.join(' '));
    await refreshSelected();
  };

  const releaseCover = async (cover: ScheduleCover, end: boolean) => {
    if (!selectedSchedule) return;
    const who = `${cover.replacementUserName || userLabel(cover.replacementUserId)} covering ${cover.coveredUserName || userLabel(cover.coveredUserId)}`;
    const ok = await confirmDialog(
      end
        ? `End the cover (${who}) now? ${cover.coveredUserName || userLabel(cover.coveredUserId)} returns to the roster for the remaining time.`
        : `Cancel the upcoming cover (${who})? The roster is used as normal.`,
      { title: end ? 'End cover' : 'Cancel cover', confirmLabel: end ? 'End now' : 'Cancel cover', danger: true },
    );
    if (!ok) return;
    setCoverBusyId(cover.coverId);
    setActionError(null);
    try {
      if (end) await tenantApi.endScheduleCover(selectedSchedule.scheduleId, cover.coverId);
      else await tenantApi.cancelScheduleCover(selectedSchedule.scheduleId, cover.coverId);
      setActionNotice(end ? `Cover ended: ${who}.` : `Cover cancelled: ${who}.`);
      await refreshSelected();
    } catch (err: any) {
      setActionError(err?.message || (end ? 'Failed to end cover' : 'Failed to cancel cover'));
    } finally {
      setCoverBusyId(null);
    }
  };

  /** Adds the cover/override description to timeline segments so tooltips read "E covering B – reason" or "Team override". */
  const withCoverLabels = (entries: ScheduleTimelineEntry[]): LinearTimelineEntry[] => entries.map(e => {
    const kind = String(e.source || '').toLowerCase();
    if (kind === 'override') return { ...e, sourceLabel: `Team override → ${e.userName}` };
    if (kind !== 'cover') return e;
    const cover = e.sourceId ? coversById.get(e.sourceId.toLowerCase()) : undefined;
    return { ...e, sourceLabel: describeCoverSegment(cover, userLabel) };
  });

  const userMap = useMemo(() => new Map(users.map(u => [u.userId, u])), [users]);
  const rotationNameById = useMemo(() => {
    const map = new Map<string, string>();
    Object.values(rotationsByWindow).flat().forEach((rc) => {
      map.set(rc.rotation.rotationId, rc.rotation.name);
    });
    return map;
  }, [rotationsByWindow]);
  const rotationConfigById = useMemo(() => {
    const map = new Map<string, RotationConfig>();
    [...Object.values(rotationsByWindow).flat(), ...Object.values(linkedRotationsByWindow).flat()].forEach((rc) => {
      map.set(rc.rotation.rotationId, rc);
    });
    return map;
  }, [rotationsByWindow, linkedRotationsByWindow]);
  const timelineUsersByRotationId = useMemo(() => {
    const byRotationId = new Map<string, string[]>();
    for (const t of timeline) {
      if (!t.userName) continue;
      if (t.rotationId) {
        const key = String(t.rotationId).toLowerCase();
        const list = byRotationId.get(key) || [];
        if (!list.includes(t.userName)) list.push(t.userName);
        byRotationId.set(key, list);
      }
    }
    return byRotationId;
  }, [timeline]);
  const scheduleTimezone = selectedSchedule?.timezone || 'UTC';
  const viewportTz = scheduleTimezone || viewerTimezone || 'UTC';
  const parseUtcDate = (iso: string) => {
    if (!iso) return new Date(iso);
    if (/[zZ]$/.test(iso) || /[+-]\d\d:\d\d$/.test(iso))
      return new Date(iso);
    return new Date(`${iso}Z`);
  };
  const roundUpToMinute = (date: Date) => {
    const normalized = new Date(date.getTime());
    if (normalized.getSeconds() === 0 && normalized.getMilliseconds() === 0) return normalized;
    normalized.setSeconds(0, 0);
    normalized.setMinutes(normalized.getMinutes() + 1);
    return normalized;
  };
  const fmtLocal = (iso: string) => formatDateTimeInTimezone(roundUpToMinute(parseUtcDate(iso)), viewportTz);
  const fmtSchedule = (iso: string) => formatDateTimeInTimezone(roundUpToMinute(parseUtcDate(iso)), scheduleTimezone);
  const selectedPrimaryWindowName = useMemo(() => {
    if (!selectedSchedule?.primaryWindowId) return null;
    return windows.find(w => w.windowId === selectedSchedule.primaryWindowId)?.name || null;
  }, [selectedSchedule?.primaryWindowId, windows]);

  const calendarGridDays = useMemo(() => {
    const days: Date[] = [];
    const start = new Date();
    const startDayUtc = alignToLocalMidnightUtc(start, viewportTz);
    for (let i = 0; i < calendarSpanDays; i++) {
      days.push(new Date(startDayUtc.getTime() + i * 86400000));
    }
    return days;
  }, [calendarSpanDays, viewportTz]);

  const linearRows = useMemo(() => {
    const effectiveTimeline = timeline;
    const grouped = new Map<string, { rotationId: string; rotationName: string; items: ScheduleTimelineEntry[] }>();
    effectiveTimeline.forEach((t) => {
      const key = `${t.rotationId}::${t.rotationName}`;
      if (!grouped.has(key)) grouped.set(key, { rotationId: t.rotationId, rotationName: t.rotationName, items: [] });
      grouped.get(key)!.items.push(t);
    });

    return Array.from(grouped.values())
      .sort((a, b) => a.rotationName.localeCompare(b.rotationName))
      .map((row) => {
        const dayCells = calendarGridDays.map((day) => {
          const dayStart = day;
          const dayEnd = new Date(dayStart.getTime() + 86400000);
          const assignments = row.items
            .filter((t) => new Date(t.startUtc) < dayEnd && new Date(t.endUtc) > dayStart)
            .sort((a, b) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime());
          const cellGaps = gaps.filter((g) => new Date(g.startUtc) < dayEnd && new Date(g.endUtc) > dayStart);
          return { dayStart, dayEnd, assignments, gaps: cellGaps };
        });

        return { ...row, dayCells };
      });
  }, [timeline, rotationsByWindow, windows, calendarGridDays, gaps, users, scheduleTimezone]);

  const windowTimelineRows = useMemo(() => {
    const allRows = linearRows.map((r) => ({
      rotationId: r.rotationId,
      rotationName: r.rotationName,
      items: r.items,
    }));

    return windows.map((w) => {
      const rotationIds = new Set((rotationsByWindow[w.windowId] || []).map((r) => r.rotation.rotationId));
      const rows = allRows.filter((row) => rotationIds.has(row.rotationId));
      return { window: w, rows };
    });
  }, [linearRows, windows, rotationsByWindow]);

  const linkedWindowTimelineRows = useMemo(() => {
    const grouped = new Map<string, { rotationId: string; rotationName: string; items: ScheduleTimelineEntry[] }>();
    linkedTimeline.forEach((t) => {
      const key = `${t.rotationId}::${t.rotationName}`;
      if (!grouped.has(key)) grouped.set(key, { rotationId: t.rotationId, rotationName: t.rotationName, items: [] });
      grouped.get(key)!.items.push(t);
    });

    const rows = Array.from(grouped.values())
      .sort((a, b) => a.rotationName.localeCompare(b.rotationName))
      .map((row) => ({ ...row }));

    return linkedWindows.map((w) => {
      const rotationIds = new Set((linkedRotationsByWindow[w.windowId] || []).map((r) => r.rotation.rotationId));
      const windowRows = rows.filter((row) => rotationIds.has(row.rotationId));
      return { window: w, rows: windowRows };
    });
  }, [linkedTimeline, linkedWindows, linkedRotationsByWindow]);

  const timelinePanels = useMemo(() => {
    const primaryPanels = windowTimelineRows.map(({ window, rows }) => ({
      key: `primary-${window.windowId}`,
      kind: 'primary' as const,
      window,
      rows,
      scheduleId: selectedSchedule?.scheduleId || '',
      scheduleName: selectedSchedule?.name || 'Primary Schedule',
    }));

    const escalationPanels = linkedEscalationSchedule
      ? linkedWindowTimelineRows.map(({ window, rows }) => ({
          key: `escalation-${window.windowId}`,
          kind: 'escalation' as const,
          window,
          rows,
          scheduleId: linkedEscalationSchedule.scheduleId,
          scheduleName: linkedEscalationSchedule.name,
        }))
      : [];

    return [...primaryPanels, ...escalationPanels];
  }, [windowTimelineRows, linkedWindowTimelineRows, selectedSchedule?.name, linkedEscalationSchedule]);

  const selectedSwapEntryKey = useMemo(() => {
    return selectedSwapSource ? toTimelineEntryKey(selectedSwapSource.entry) : null;
  }, [selectedSwapSource]);

  const buildSwapSelection = (
    scheduleId: string,
    scheduleName: string,
    windowId: string,
    windowName: string,
    entry: LinearTimelineEntry,
  ): TimelineSwapSelection => {
    const rotationConfig = rotationConfigById.get(entry.rotationId);
    const rotationGroupId = rotationConfig?.rotation.groupId;
    return {
      scheduleId,
      scheduleName,
      windowId,
      windowName,
      entry,
      rotationGroupId,
      rotationGroupName: rotationGroupId ? groups.find((group) => group.groupId === rotationGroupId)?.name : undefined,
    };
  };

  const timelineSummary = useMemo(() => {
    const primaryWindowCount = windowTimelineRows.length;
    const escalationWindowCount = linkedEscalationSchedule ? linkedWindowTimelineRows.length : 0;
    const primaryEntryCount = windowTimelineRows.reduce((sum, p) => sum + p.rows.reduce((rs, r) => rs + r.items.length, 0), 0);
    const escalationEntryCount = linkedEscalationSchedule
      ? linkedWindowTimelineRows.reduce((sum, p) => sum + p.rows.reduce((rs, r) => rs + r.items.length, 0), 0)
      : 0;

    return {
      primaryWindowCount,
      escalationWindowCount,
      primaryEntryCount,
      escalationEntryCount,
      totalPanels: primaryWindowCount + escalationWindowCount,
    };
  }, [windowTimelineRows, linkedWindowTimelineRows, linkedEscalationSchedule]);

  const timelineBlockers = useMemo(() => {
    const allRotations = Object.values(rotationsByWindow).flat();
    const missingRoster = allRotations.filter(r => !r.roster || r.roster.length === 0).map(r => r.rotation.name);
    const missingRules = allRotations.filter(r => !r.rules || r.rules.length === 0).map(r => r.rotation.name);
    return {
      totalRotations: allRotations.length,
      missingRoster,
      missingRules,
      scheduleInactive: selectedSchedule ? !selectedSchedule.isActive : false,
    };
  }, [rotationsByWindow, selectedSchedule]);

  const timelineDebugRows = useMemo(() => {
    const visibleStart = calendarGridDays[0]?.getTime();
    const visibleEnd = calendarGridDays.length > 0 ? calendarGridDays[calendarGridDays.length - 1].getTime() + 86400000 : undefined;

    const rotationToWindow = new Map<string, string>();
    const rotationNameToWindow = new Map<string, string>();
    windows.forEach((w) => {
      (rotationsByWindow[w.windowId] || []).forEach((rc) => {
        rotationToWindow.set(rc.rotation.rotationId, w.name);
        if (rc.rotation.name) {
          rotationNameToWindow.set(rc.rotation.name, w.name);
        }
      });
    });

    return timeline
      .filter((t) => {
        if (visibleStart === undefined || visibleEnd === undefined) return true;
        const s = new Date(t.startUtc).getTime();
        const e = new Date(t.endUtc).getTime();
        return s < visibleEnd && e > visibleStart;
      })
      .map((t) => {
        const s = new Date(t.startUtc).getTime();
        const e = new Date(t.endUtc).getTime();
        const minutes = Math.max(0, Math.round((e - s) / 60000));
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return {
          ...t,
          windowName: rotationToWindow.get(t.rotationId) || rotationNameToWindow.get(t.rotationName) || 'Unknown',
          durationLabel: `${hrs}h ${String(mins).padStart(2, '0')}m`,
        };
      })
      .sort((a, b) => {
        const s = new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime();
        if (s !== 0) return s;
        return a.rotationName.localeCompare(b.rotationName);
      });
  }, [timeline, windows, rotationsByWindow, calendarGridDays]);

  const visibleSwaps = useMemo(() => {
    const ordered = [...swaps].sort((a, b) => {
      const requestedDiff = new Date(b.requestedAt || 0).getTime() - new Date(a.requestedAt || 0).getTime();
      if (requestedDiff !== 0) return requestedDiff;

      const startDiff = new Date(b.startUtc).getTime() - new Date(a.startUtc).getTime();
      if (startDiff !== 0) return startDiff;

      return new Date(b.endUtc).getTime() - new Date(a.endUtc).getTime();
    });

    const latestByWindow = new Map<string, ScheduleShiftSwap[]>();

    for (const swap of ordered) {
      const key = [swap.rotationId || '', swap.userAId, swap.userBId, swap.startUtc].join('|');
      const group = latestByWindow.get(key) || [];
      group.push(swap);
      latestByWindow.set(key, group);
    }

    return Array.from(latestByWindow.values()).map((group) => group[0]);
  }, [swaps]);

  const getSwapVisualState = (status?: string): SwapVisualState => {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized === 'pending') return 'pending';
    if (normalized === 'approved') return 'approved';
    if (normalized === 'applied') return 'applied';
    if (normalized === 'rejected') return 'rejected';
    if (normalized === 'cancelled') return 'cancelled';
    return 'other';
  };

  return (
    <div className="management-section schedules-management-section schedules-management-view">
      <div className="section-header">
        <h2>Schedules</h2>
        <div className="schedules-header-actions">
          <button className="btn-secondary" aria-expanded={showSchedulerHowTo}
            aria-controls="scheduler-creation-howto" onClick={() => setShowSchedulerHowTo(value => !value)}>Creation HowTo</button>
          <button className="btn-primary" onClick={() => setShowSchedulerWizard(true)}>+ Schedule wizard</button>
          <button className="btn-primary" onClick={() => setShowWindowModal(true)} disabled={!selectedSchedule}>+ Window</button>
          <details className="schedule-action-menu" onKeyDown={closeActionMenuOnEscape}>
            <summary className="btn-secondary">Roster changes</summary>
            <div className="schedule-action-menu-items" onClick={closeActionMenuOnSelection}>
              <button
                className="btn-secondary"
                onClick={() => {
                  if (!selectedSchedule) return;
                  const source = selectedSwapSource && selectedSwapSource.scheduleId === selectedSchedule.scheduleId
                    ? selectedSwapSource
                    : null;
                  setSwapModalContext({ scheduleId: selectedSchedule.scheduleId, source });
                }}
                disabled={!selectedSchedule}
              >+ Swap</button>
              <button className="btn-secondary" onClick={() => openCoverModal()}
                disabled={!selectedSchedule || !canRemoveRosterMembers}
                title={canRemoveRosterMembers ? 'Replace an operator for whole rotation blocks' : 'Only schedule managers or team leads can add covers'}>Add cover</button>
            </div>
          </details>
          <details className="schedule-action-menu" onKeyDown={closeActionMenuOnEscape}>
            <summary className="btn-secondary">Advanced</summary>
            <div className="schedule-action-menu-items" onClick={closeActionMenuOnSelection}>
              <button className="btn-secondary" title="Advanced schedule creation" onClick={() => setShowCreateScheduleModal(true)}>Create schedule manually</button>
            </div>
          </details>
        </div>
      </div>

      <div className="schedules-management-scroll">
      {showSchedulerHowTo && <div id="scheduler-creation-howto"><SchedulerWizardHowTo /></div>}
      {loading ? <div className="loading">Loading scheduler...</div> : (
        <>
          <div className="schedule-selection-card">
            <div className="schedule-selection-main">
              <label htmlFor="managed-schedule">Schedule</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  id="managed-schedule"
                  value={selectedSchedule?.scheduleId || ''}
                  onChange={async (e) => {
                    const s = schedules.find(x => x.scheduleId === e.target.value) || null;
                    if (s) await selectSchedule(s);
                  }}
                >
                  <option value="">Select schedule...</option>
                  {schedules.map(s => {
                    const onCall = onCallData?.allSchedules?.find(o => o.scheduleId === s.scheduleId);
                    const typeLabel = s.scheduleType || 'Primary';
                    return (
                      <option key={s.scheduleId} value={s.scheduleId}>
                        {s.name} {s.isActive ? '(Active)' : '(Inactive)'}
                        {` • ${typeLabel}`}
                        {onCall ? ` → On-call: ${onCall.userName || onCall.userEmail || 'Unknown'}` : ''}
                      </option>
                    );
                  })}
                </select>
                <button className="btn-icon" title="Edit schedule" disabled={!selectedSchedule} onClick={() => selectedSchedule && setEditScheduleModal(selectedSchedule)}>✏️</button>
                <button
                  className="btn-icon"
                  title="Delete schedule"
                  disabled={!selectedSchedule}
                  onClick={async () => {
                    if (!selectedSchedule) return;
                    const schedule = selectedSchedule;
                    if (!await confirmDialog(
                      `Delete "${schedule.name}" and all associated windows, rotations, rosters, overrides and schedule history? This cannot be undone. Linked escalation schedules must be unlinked or deleted first.`,
                      { title: 'Delete schedule', confirmLabel: 'Delete schedule', danger: true },
                    )) return;
                    setActionError(null);
                    try {
                      await tenantApi.deleteSchedule(schedule.scheduleId);
                      await init();
                    } catch (err: any) {
                      const message = err?.message || 'Failed to delete schedule';
                      setActionError(message);
                    }
                  }}
                >🗑️</button>
                {selectedSchedule && (
                  <span style={{ alignSelf: 'center', fontSize: 12, padding: '0.2rem 0.5rem', borderRadius: 999, background: selectedSchedule.isActive ? 'var(--color-primary-light)' : 'var(--color-border-light)', color: selectedSchedule.isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)' }}>
                    {selectedSchedule.isActive ? 'Active' : 'Inactive'}
                  </span>
                )}
                <button className="btn-secondary" onClick={refreshSelected} disabled={!selectedSchedule}>Refresh</button>
              </div>
              {selectedSchedule && (
                <div className="help-text schedule-selection-summary">
                  {`${selectedSchedule.scheduleType || 'Primary'} • ${selectedPrimaryWindowName || 'No primary window'} • `}
                  {windows.length > 0
                    ? `${DOW[windows[0].startDayOfWeek]} ${formatWallTime(windows[0].startTimeLocal)}, ${windows[0].durationValue} ${windows[0].durationUnit}`
                    : 'No window configured yet'}
                  {` • ${scheduleTimezone}`}
                </div>
              )}
            </div>
            {selectedSchedule?.scheduleType === 'Primary' && (
              <div className="schedule-selection-link">
                <label htmlFor="linked-escalation">Linked escalation</label>
                <div className="schedule-selection-link-controls">
                  <select
                    id="linked-escalation"
                    value={linkedEscalationScheduleId}
                    onChange={async (e) => {
                      const id = e.target.value;
                      await loadLinkedEscalation(id);
                    }}
                  >
                    <option value="">None</option>
                    {schedules
                      .filter(s => s.scheduleType === 'Escalation' && s.scheduleId !== selectedSchedule.scheduleId)
                      .map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.name}</option>)}
                  </select>
                  {linkedEscalationScheduleId !== savedLinkedEscalationScheduleId && <button
                      className="btn-secondary"
                      disabled={savingLinkedEscalation}
                      onClick={saveLinkedEscalationLink}
                    >
                      {savingLinkedEscalation ? 'Saving...' : 'Save Link'}
                    </button>}
                </div>
                {linkedEscalationScheduleId !== savedLinkedEscalationScheduleId && (
                  <span className="schedule-link-status heuristic">Unsaved link selection</span>
                )}
                {savedLinkedEscalationScheduleId && linkedEscalationScheduleId === savedLinkedEscalationScheduleId && (
                  <span className="schedule-link-status">Linked escalation schedule persisted</span>
                )}
              </div>
            )}
          </div>
          {selectedSchedule && <OnCallPresencePanel key={selectedSchedule.scheduleId} scheduleId={selectedSchedule.scheduleId} compact />}

          {actionNotice && (
            <div
              role="status"
              className="roster-action-notice"
              style={{
                marginBottom: '0.75rem',
                padding: '0.6rem 0.75rem',
                borderRadius: 8,
                border: '1px solid rgba(34,197,94,0.35)',
                background: 'rgba(34,197,94,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>{actionNotice}</span>
              <button type="button" className="btn-icon" title="Dismiss" onClick={() => setActionNotice(null)}>✕</button>
            </div>
          )}

          {actionError && (
            <div
              role="alert"
              style={{
                marginBottom: '0.75rem',
                padding: '0.6rem 0.75rem',
                borderRadius: 8,
                border: '1px solid rgba(239,68,68,0.35)',
                background: 'rgba(239,68,68,0.08)',
                color: 'var(--color-severity-critical)',
              }}
            >
              {actionError}
            </div>
          )}

          <div className="data-table" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Windows & Rotations</h3>
            {windows.length === 0 ? <div className="empty-row">No windows yet.</div> : windows.map(w => (
              <div key={w.windowId} style={{ border: '1px solid var(--border-color,#e2e8f0)', borderRadius: 8, marginBottom: 10, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{w.name}</strong> — {DOW[w.startDayOfWeek] || w.startDayOfWeek} {formatWallTime(w.startTimeLocal)}, {w.durationValue} {w.durationUnit}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-icon" title="Edit" onClick={() => { setWindowTarget(w); setShowWindowModal(true); }}>✏️</button>
                    <button className="btn-icon" title="Add rotation" onClick={() => setShowRotationModal({ windowId: w.windowId })}>+🔁</button>
                    <button
                      className="btn-icon"
                      title="Delete window"
                      onClick={async () => {
                        if (!selectedSchedule) return;
                        if (!await confirmDialog(
                          `Delete "${w.name}" and all associated rotations/rosters? This cannot be undone.`,
                          { title: 'Delete window', confirmLabel: 'Delete window', danger: true },
                        )) return;
                        try {
                          await tenantApi.deleteScheduleWindow(selectedSchedule.scheduleId, w.windowId);
                          await refreshSelected();
                        } catch (err: any) {
                          setActionError(err?.message || 'Failed to delete window');
                        }
                      }}
                    >🗑️</button>
                  </div>
                </div>

                <table className="data-table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Rotation</th>
                      <th>Group</th>
                      <th>Rules</th>
                      <th>Rostering</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rotationsByWindow[w.windowId] || []).map(rc => (
                      <tr key={rc.rotation.rotationId}>
                        <td>{rc.rotation.name}</td>
                        <td>{groups.find(g => g.groupId === rc.rotation.groupId)?.name || 'Unknown group'}</td>
                        <td>
                          {(rc.rules || []).map(r => {
                            const endLabel = formatRuleEndLabel(r, resolveWindowDurationMinutes(w));
                            return `${DOW[r.dayOfWeek]} ${formatWallTime(r.startTimeLocal)} -> ${endLabel}`;
                          }).join(', ') || '—'}
                        </td>
                        <td>
                          {tryParseLockTargetRotationId(rc.rotation.lockKey) ? (
                            <div>
                              <span className="btn-secondary" style={{ padding: '0.25rem 0.5rem', cursor: 'default' }}>Roster Lock</span>
                              <div className="help-text" style={{ margin: '0.35rem 0 0' }}>
                                {rotationNameById.get(tryParseLockTargetRotationId(rc.rotation.lockKey) || '') || 'Locked rotation'}
                              </div>
                            </div>
                          ) : (
                            <div>
                              {(rc.roster || []).length === 0 && (() => {
                                const firstGap = gaps
                                  .filter(g => g.windowId === w.windowId)
                                  .map(g => g.startUtc)
                                  .sort()[0];
                                const from = firstGap ? fmtSchedule(firstGap) : fmtSchedule(new Date().toISOString());
                                return (
                                  <div className="error-banner roster-empty-banner" role="alert" style={{ marginBottom: 6 }}>
                                    No members – coverage gap from {from}
                                  </div>
                                );
                              })()}
                              {(() => {
                                const flagged = findRosterAttention(rc.roster || [], users);
                                if (flagged.size === 0) return null;
                                return (
                                  <div className="roster-attention" style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {(rc.roster || []).filter(r => flagged.has(r.userId)).map(r => (
                                      <span key={r.userId} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                        <span className="badge badge-warning" title="Inactive users cannot be on call">⚠ Inactive: {r.userName || r.userEmail || r.userId}</span>
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          style={{ padding: '0.1rem 0.4rem' }}
                                          disabled={!canRemoveRosterMembers}
                                          title={canRemoveRosterMembers ? 'Remove from roster' : ROSTER_REMOVAL_DENIED_TOOLTIP}
                                          onClick={() => openRosterRemoval(rc, r.userId, r.userName || r.userEmail || r.userId)}
                                        >Remove from roster</button>
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                              <div className="help-text" style={{ marginBottom: 6 }}>
                                {(() => {
                                  const rosterNames = (rc.roster || [])
                                    .map((r) => r.userName || r.userEmail || r.userId)
                                    .filter(Boolean);
                                  const rotationIdKey = String(rc.rotation.rotationId || '').toLowerCase();
                                  const timelineNames = timelineUsersByRotationId.get(rotationIdKey) || [];
                                  const names = rosterNames.length > 0 ? rosterNames : timelineNames;
                                  return names.length > 0
                                    ? (rosterNames.length > 0
                                      ? `${names.length} users: ${names.join(', ')}`
                                      : `No roster assigned in API (timeline users: ${names.join(', ')})`)
                                    : 'No roster assigned';
                                })()}
                              </div>
                              <button
                                className="btn-secondary"
                                style={{ padding: '0.25rem 0.5rem' }}
                                onClick={() => openRosterEditor(rc, w)}
                              >Roster Admin</button>
                              <div className="help-text" style={{ marginTop: 6 }}>
                                Team Leads: use Users and Groups, edit this roster group, and tick Team Lead for selected members.
                              </div>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="rotation-actions">
                          <button className="btn-icon" title="Edit rotation" onClick={() => setEditRotationTarget({ windowId: w.windowId, config: rc })}>✏️</button>
                          <button
                            className="btn-icon"
                            title="Delete rotation"
                            disabled={(rotationsByWindow[w.windowId] || []).length <= 1}
                            onClick={async () => {
                              if (!selectedSchedule) return;
                              if ((rotationsByWindow[w.windowId] || []).length <= 1) {
                                setActionError('Cannot delete the last rotation in a window. Add another rotation first or delete the window.');
                                return;
                              }
                              if (!await confirmDialog(
                                `Delete "${rc.rotation.name}" and its roster? This cannot be undone.`,
                                { title: 'Delete rotation', confirmLabel: 'Delete rotation', danger: true },
                              )) return;
                              try {
                                await tenantApi.deleteScheduleRotation(selectedSchedule.scheduleId, w.windowId, rc.rotation.rotationId);
                                await refreshSelected();
                              } catch (err: any) {
                                setActionError(err?.message || 'Failed to delete rotation');
                              }
                            }}
                          >🗑️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(rotationsByWindow[w.windowId] || []).length === 0 && <tr><td colSpan={5} className="empty-row">No rotations</td></tr>}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <div className="data-table schedule-timeline-panel" ref={timelinePanelRef} style={{ padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>Timeline</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                <button className="btn-secondary" aria-expanded={replacementHelp !== null}
                  aria-controls="schedule-replacement-howto" onClick={() => replacementHelp === null ? openReplacementHelp('overview') : setReplacementHelp(null)}>Replacement HowTo</button>
                <button className={timelineSpanDays === 1 ? 'week-btn active' : 'week-btn'} onClick={() => setTimelineSpanDays(1)}>1D</button>
                <button className={timelineSpanDays === 7 ? 'week-btn active' : 'week-btn'} onClick={() => setTimelineSpanDays(7)}>1W</button>
                <button className={timelineSpanDays === 14 ? 'week-btn active' : 'week-btn'} onClick={() => setTimelineSpanDays(14)}>2W</button>
                <button className={timelineSpanDays === 30 ? 'week-btn active' : 'week-btn'} onClick={() => setTimelineSpanDays(30)}>1M</button>
              </div>
            </div>

            {replacementHelp !== null && <div id="schedule-replacement-howto"><ScheduleReplacementHowTo
              topic={replacementHelp} onTopicChange={setReplacementHelp} onClose={() => setReplacementHelp(null)}
              otherPage="On-call Overrides" onOtherPage={onOpenOverrides} /></div>}
            <div className="help-text" style={{ marginBottom: 8 }}>
              Timeline range uses your timezone: {viewportTz}. Schedule timezone: {scheduleTimezone}.
            </div>

            <div className="timeline-source-legend" aria-label="Timeline legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, fontSize: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span aria-hidden="true" style={{ width: 18, height: 10, borderRadius: 3, background: 'hsl(210 70% 47%)' }} />
                Roster / swap
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span aria-hidden="true" style={{ width: 18, height: 10, borderRadius: 3, border: '1px dashed var(--color-text-secondary)', background: timelineSourceBackground('cover', 'hsl(210 70% 47%)') }} />
                Cover
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span aria-hidden="true" style={{ width: 18, height: 10, borderRadius: 3, border: '1px solid #fbbf24', background: timelineSourceBackground('override', OVERRIDE_SEGMENT_COLOR) }} />
                Team override
              </span>
            </div>

            <div className="schedule-timeline-summary" style={{ marginBottom: 8 }}>
              <span className="schedule-link-status">Primary windows: {timelineSummary.primaryWindowCount}</span>
              <span className="schedule-link-status">Primary allocations: {timelineSummary.primaryEntryCount}</span>
              {linkedEscalationSchedule && (
                <>
                  <span className="schedule-link-status">Escalation windows: {timelineSummary.escalationWindowCount}</span>
                  <span className="schedule-link-status">Escalation allocations: {timelineSummary.escalationEntryCount}</span>
                </>
              )}
            </div>

            {linearRows.length === 0 && (
              <div style={{ marginBottom: 8, padding: 10, border: '1px solid var(--border-color,#e2e8f0)', borderRadius: 8, background: 'var(--card-bg)' }}>
                <div><strong>Why timeline may be empty:</strong></div>
                {timelineBlockers.totalRotations === 0 && <div className="help-text">No rotations configured in this schedule window.</div>}
                {timelineBlockers.scheduleInactive && <div className="help-text">Schedule is currently inactive.</div>}
                {timelineBlockers.missingRoster.length > 0 && <div className="help-text">Missing roster: {timelineBlockers.missingRoster.join(', ')}</div>}
                {timelineBlockers.missingRules.length > 0 && <div className="help-text">Missing rules: {timelineBlockers.missingRules.join(', ')}</div>}
                <div className="help-text">Also verify API timeline data by clicking Refresh after any window/rotation/roster changes.</div>
              </div>
            )}
            {timelineSummary.totalPanels === 0 ? (
              <div className="empty-row">No windows available for timeline view.</div>
            ) : (
              <div className="schedule-window-panels">
                {timelinePanels.map(({ key, kind, window, rows, scheduleName, scheduleId }) => {
                  const windowEntries = withCoverLabels(rows.flatMap((r) => r.items));
                  const selectedForPanel = selectedSwapSource
                    && selectedSwapSource.scheduleId === scheduleId
                    && selectedSwapSource.windowId === window.windowId
                    ? selectedSwapSource
                    : null;
                  return (
                    <div key={key} className="schedule-window-panel">
                      <div className="schedule-window-panel-header-row">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="schedule-window-panel-header">{window.name}</div>
                          <span className={`schedule-window-kind-badge ${kind}`}>
                            {kind === 'primary' ? 'Primary' : 'Escalation'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {kind === 'primary' && (
                            <button
                              className="btn-secondary"
                              style={{ padding: '0.25rem 0.5rem' }}
                              disabled={!selectedForPanel || !canRemoveRosterMembers}
                              title={!canRemoveRosterMembers
                                ? 'Only schedule managers or team leads can add covers'
                                : selectedForPanel ? 'Cover this operator\'s rotation block with a replacement' : 'Select a timeline shift in this window first'}
                              onClick={() => {
                                if (!selectedForPanel) return;
                                openCoverModal({
                                  coveredUserId: selectedForPanel.entry.userId,
                                  rotationId: null,
                                  focusUtc: selectedForPanel.entry.startUtc,
                                });
                              }}
                            >Cover selected block</button>
                          )}
                          <button
                            className="btn-secondary"
                            style={{ padding: '0.25rem 0.5rem' }}
                            onClick={() => {
                              if (!selectedForPanel) {
                                void alertDialog('Select a timeline shift in this window first, then click Swap.', { title: 'Select a shift to swap' });
                                return;
                              }
                              setSwapModalContext({ scheduleId, source: selectedForPanel });
                            }}
                          >Swap selected shift</button>
                        </div>
                      </div>
                      <div className="help-text" style={{ marginBottom: 6 }}>
                        {scheduleName}
                      </div>
                      {selectedForPanel && (
                        <div className="help-text" style={{ marginBottom: 6 }}>
                          Selected: {selectedForPanel.entry.userName} ({fmtSchedule(selectedForPanel.entry.startUtc)} {'->'} {fmtSchedule(selectedForPanel.entry.endUtc)})
                        </div>
                      )}
                      <ScheduleLinearTimeline
                        entries={windowEntries}
                        timezone={viewportTz}
                        spanDays={timelineSpanDays}
                        onSpanDaysChange={setTimelineSpanDays}
                        showControls={false}
                        defaultSpanDays={7}
                        lockViewport={true}
                        selectedEntryKey={selectedSwapEntryKey}
                        onEntrySelect={(entry) => {
                          setSelectedSwapSource(buildSwapSelection(scheduleId, scheduleName, window.windowId, window.name, entry));
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedSchedule && (
            <ScheduleCoversPanel
              covers={covers}
              timezone={scheduleTimezone}
              rotationNameById={rotationNameById}
              resolveUserLabel={userLabel}
              canManage={canRemoveRosterMembers}
              busyCoverId={coverBusyId}
              onAdd={() => openCoverModal()}
              onHelp={() => openReplacementHelp('cover')}
              onEnd={(cover) => releaseCover(cover, true)}
              onCancel={(cover) => releaseCover(cover, false)}
            />
          )}

          <div className="data-table" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ marginTop: 0, marginBottom: 0 }}>Allocation Trace (API rows)</h3>
              <button className="btn-secondary" onClick={() => setShowTimelineDebug((v) => !v)}>
                {showTimelineDebug ? 'Hide' : 'Show'}
              </button>
            </div>
            {showTimelineDebug && (
              timelineDebugRows.length === 0 ? (
                <div className="empty-row">No allocation rows returned for the visible range.</div>
              ) : (
                <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border-color,#e2e8f0)', borderRadius: 8 }}>
                  <table className="data-table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Start</th>
                        <th>End</th>
                        <th>Duration</th>
                        <th>Window</th>
                        <th>Rotation</th>
                        <th>User</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timelineDebugRows.map((r, idx) => (
                        <tr key={`${r.rotationId}-${r.userId}-${r.startUtc}-${idx}`}>
                          <td>{fmtLocal(r.startUtc)}</td>
                          <td>{fmtLocal(r.endUtc)}</td>
                          <td>{r.durationLabel}</td>
                          <td>{r.windowName}</td>
                          <td>{r.rotationName}</td>
                          <td>{r.userName}</td>
                          <td>{r.source || 'roster'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          <div className="data-table" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Gaps</h3>
            {gaps.length === 0 ? <div className="empty-row">No gaps detected</div> : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {gaps.map((g, idx) => (
                    <tr key={`${g.startUtc}-${idx}`}>
                      <td>{fmtLocal(g.startUtc)}</td>
                      <td>{fmtLocal(g.endUtc)}</td>
                      <td>{g.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="data-table" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: 8 }}>
              <div>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>Swaps</h3>
                <div className="help-text" style={{ marginBottom: 0 }}>
                  Swap times use the schedule timezone: {selectedSchedule?.timezone || 'UTC'}.
                </div>
                <button className="btn-secondary" onClick={() => openReplacementHelp('swap')}>Swap HowTo</button>
                <div className="swap-status-legend">
                  <span className="legend-chip pending">Pending approval</span>
                  <span className="legend-chip applied">Completed</span>
                  <span className="legend-chip rejected">Rejected/Cancelled</span>
                </div>
              </div>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border-color,#e2e8f0)', borderRadius: 8 }}>
              <table className="data-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Window</th>
                    <th>User A</th>
                    <th>User B</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSwaps.map(s => {
                    const visualState = getSwapVisualState(s.status);
                    return (
                      <tr key={s.swapId} className={`swap-row swap-row-${visualState}`}>
                        <td>{fmtSchedule(s.startUtc)} → {fmtSchedule(s.endUtc)}</td>
                        <td>{userMap.get(s.userAId)?.displayName || userMap.get(s.userAId)?.email || s.userAId}</td>
                        <td>{userMap.get(s.userBId)?.displayName || userMap.get(s.userBId)?.email || s.userBId}</td>
                        <td>
                          <span className={`status-badge swap-status-badge ${visualState}`}>{s.status}</span>
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-secondary" disabled={!selectedSchedule || s.status !== 'Pending' || s.canApprove !== true} onClick={async () => { if (!selectedSchedule) return; await tenantApi.approveScheduleSwap(selectedSchedule.scheduleId, s.swapId); await refreshSelected(); }}>Approve</button>
                          <button className="btn-secondary" disabled={!selectedSchedule || s.status !== 'Pending' || s.canReject !== true} onClick={async () => { if (!selectedSchedule) return; await tenantApi.rejectScheduleSwap(selectedSchedule.scheduleId, s.swapId); await refreshSelected(); }}>Reject</button>
                          <button className="btn-secondary" disabled={!selectedSchedule || s.status !== 'Applied'} onClick={async () => {
                            if (!selectedSchedule) return;
                            if (!await confirmDialog('Revert this applied swap and restore the original shift assignment?', {
                              title: 'Revert shift swap', confirmLabel: 'Revert swap', danger: true,
                            })) return;
                            await tenantApi.revertScheduleSwap(selectedSchedule.scheduleId, s.swapId);
                            await refreshSelected();
                          }}>Revert</button>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleSwaps.length === 0 && <tr><td colSpan={5} className="empty-row">No swaps</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      </div>

      {showCreateScheduleModal && <CreateScheduleModal schedules={schedules} onClose={() => setShowCreateScheduleModal(false)} onCreated={async () => { setShowCreateScheduleModal(false); await init(); }} />}
      {showSchedulerWizard && <SchedulerWizard onClose={() => setShowSchedulerWizard(false)} onCreated={async scheduleId => {
        const data = await tenantApi.getSchedules();
        setSchedules(data);
        const created = data.find(schedule => schedule.scheduleId === scheduleId);
        if (!created) throw new Error('Created schedule was not returned by the refreshed list.');
        await selectSchedule(created, data);
      }} />}
      {editScheduleModal && (
        <EditScheduleModal
          schedule={editScheduleModal}
          schedules={schedules}
          onClose={() => setEditScheduleModal(null)}
          onSaved={async () => { setEditScheduleModal(null); await init(); }}
        />
      )}
      {showWindowModal && selectedSchedule && (
        <WindowModal
          confirmDelete={() => confirmDialog(
            `Delete "${windowTarget?.name}" and all associated rotations/rosters? This cannot be undone.`,
            { title: 'Delete window', confirmLabel: 'Delete window', danger: true },
          )}
          scheduleId={selectedSchedule.scheduleId}
          window={windowTarget || undefined}
          onClose={() => { setShowWindowModal(false); setWindowTarget(null); }}
          onSaved={async () => { setShowWindowModal(false); setWindowTarget(null); await refreshSelected(); }}
          onDeleted={async () => { setShowWindowModal(false); setWindowTarget(null); await refreshSelected(); }}
        />
      )}
      {showRotationModal && selectedSchedule && (
        <RotationModal
          scheduleId={selectedSchedule.scheduleId}
          windowId={showRotationModal.windowId}
          windowDurationMinutes={resolveWindowDurationMinutes(windows.find(w => w.windowId === showRotationModal.windowId))}
          groups={groups}
          lockTargets={Object.values(rotationsByWindow).flat().map(r => ({ rotationId: r.rotation.rotationId, name: r.rotation.name }))}
          onClose={() => setShowRotationModal(null)}
          onSaved={async () => { setShowRotationModal(null); await refreshSelected(); }}
        />
      )}
      {editRotationTarget && selectedSchedule && (
        <RotationModal
          scheduleId={selectedSchedule.scheduleId}
          windowId={editRotationTarget.windowId}
          windowDurationMinutes={resolveWindowDurationMinutes(windows.find(w => w.windowId === editRotationTarget.windowId))}
          groups={groups}
          lockTargets={Object.values(rotationsByWindow).flat().map(r => ({ rotationId: r.rotation.rotationId, name: r.rotation.name }))}
          existing={editRotationTarget.config}
          onClose={() => setEditRotationTarget(null)}
          onSaved={async () => { setEditRotationTarget(null); await refreshSelected(); }}
        />
      )}
      {swapModalContext && (
        <SwapModal
          scheduleId={swapModalContext.scheduleId}
          scheduleTimezone={selectedSchedule?.timezone || 'UTC'}
          users={users}
          windows={windows}
          source={swapModalContext.source}
          onClose={() => setSwapModalContext(null)}
          onSaved={async () => {
            setSwapModalContext(null);
            await refreshSelected();
          }}
        />
      )}
      {rosterEditorTarget && selectedSchedule && (
        <RosterEditorModal
          scheduleId={selectedSchedule.scheduleId}
          timezone={selectedSchedule.timezone || 'UTC'}
          config={rosterEditorTarget.config}
          window={rosterEditorTarget.window}
          groupMembers={rosterGroupMembers}
          users={users}
          canRemoveMembers={canRemoveRosterMembers}
          onRequestRemove={(userId, label) => openRosterRemoval(rosterEditorTarget.config, userId, label)}
          onRequestCover={(userId) => {
            setRosterEditorTarget(null);
            setRosterGroupMembers([]);
            openCoverModal({ coveredUserId: userId, rotationId: null });
          }}
          onClose={() => { setRosterEditorTarget(null); setRosterGroupMembers([]); }}
          onSaved={async () => { setRosterEditorTarget(null); setRosterGroupMembers([]); await refreshSelected(); }}
        />
      )}
      {coverModal && selectedSchedule && (
        <ScheduleCoverModal
          scheduleId={selectedSchedule.scheduleId}
          timezone={selectedSchedule.timezone || 'UTC'}
          rotations={coverRotations}
          coverableUsers={coverableUsers}
          tenantUsers={coverTenantUsers}
          canForce={canForceCover}
          initial={coverModal}
          onClose={() => setCoverModal(null)}
          onCreated={onCoverCreated}
        />
      )}
      {appDialog}
      {rosterRemovalTarget && (
        <RosterRemovalModal
          scheduleId={rosterRemovalTarget.scheduleId}
          timezone={rosterRemovalTarget.timezone}
          rotationId={rosterRemovalTarget.config.rotation.rotationId}
          rotationName={rosterRemovalTarget.config.rotation.name}
          member={{ userId: rosterRemovalTarget.userId, label: rosterRemovalTarget.label }}
          resolveUserLabel={userLabel}
          onClose={() => setRosterRemovalTarget(null)}
          onRemoved={(result) => onRosterMemberRemoved(rosterRemovalTarget, result)}
        />
      )}
    </div>
  );
};

function useScheduleEditorCloseGuard(dirty: boolean, busy: boolean, onClose: () => void) {
  const { confirm, dialog } = useAppDialog();
  const confirming = useRef(false);
  const backdropStart = useRef(false);
  const requestClose = async () => {
    if (busy || confirming.current) return;
    if (!dirty) { onClose(); return; }
    confirming.current = true;
    try {
      if (await confirm('You have unsaved changes. Discard them and close this editor?', {
        title: 'Discard unsaved changes?', cancelLabel: 'Keep editing', confirmLabel: 'Discard changes', danger: true,
      })) onClose();
    } finally {
      confirming.current = false;
    }
  };
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirming.current) {
        event.preventDefault();
        void requestClose();
      }
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (!dirty && !busy) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('keydown', escape);
    window.addEventListener('beforeunload', unload);
    return () => {
      window.removeEventListener('keydown', escape);
      window.removeEventListener('beforeunload', unload);
    };
  }, [dirty, busy, onClose, confirm]);
  return {
    requestClose,
    dialog,
    backdropProps: {
      onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
        backdropStart.current = event.target === event.currentTarget;
      },
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && backdropStart.current) void requestClose();
        backdropStart.current = false;
      },
    },
  };
}

const RosterEditorModal: React.FC<{
  scheduleId: string;
  timezone: string;
  config: RotationConfig;
  window: ScheduleWindow;
  groupMembers: GroupMemberUser[];
  users: User[];
  canRemoveMembers?: boolean;
  onRequestRemove?: (userId: string, label: string) => void;
  onRequestCover?: (userId: string, label: string) => void;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ scheduleId, timezone, config, window, groupMembers, users, canRemoveMembers = false, onRequestRemove, onRequestCover, onClose, onSaved }) => {
  useDateTimePreferences();
  const [orderedUserIds, setOrderedUserIds] = useState<string[]>(config.roster.map(r => r.userId));
  const [showProjection, setShowProjection] = useState(false);
  const initialOrder = useRef(JSON.stringify(orderedUserIds));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const closeGuard = useScheduleEditorCloseGuard(JSON.stringify(orderedUserIds) !== initialOrder.current, saving, onClose);

  const userMap = useMemo(() => new Map(users.map(u => [u.userId, u])), [users]);
  const savedRosterIds = useMemo(() => new Set(config.roster.map(r => r.userId)), [config.roster]);
  const attention = useMemo(
    () => findRosterAttention(config.roster, users, groupMembers.length > 0 ? groupMembers : null),
    [config.roster, users, groupMembers],
  );
  const memberLabel = (uid: string) => {
    const u = userMap.get(uid);
    const r = config.roster.find(x => x.userId === uid);
    return u?.displayName || u?.email || r?.userName || r?.userEmail || uid;
  };
  const removeButton = (uid: string, text: string, className: string) => (
    <button
      type="button"
      className={className}
      style={className === 'btn-icon' ? undefined : { padding: '0.15rem 0.45rem' }}
      disabled={!canRemoveMembers || !onRequestRemove}
      title={canRemoveMembers ? 'Remove from roster from an effective date (with preview)' : ROSTER_REMOVAL_DENIED_TOOLTIP}
      aria-label={`Remove ${memberLabel(uid)} from roster`}
      onClick={() => onRequestRemove?.(uid, memberLabel(uid))}
    >{text}</button>
  );

  const move = (from: number, to: number) => {
    if (to < 0 || to >= orderedUserIds.length || from === to) return;
    setOrderedUserIds(prev => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, idx: number) => {
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>, idx: number) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isNaN(from)) move(from, idx);
  };

  const activeUsers = groupMembers.filter(u => (u.status || '').toLowerCase() === 'active');
  const availableToAdd = activeUsers.filter(u => !orderedUserIds.includes(u.userId));
  const durationHours = (config.rotation.assignmentDurationMinutes || 0) / 60;
  const projectionAnchorInfo = useMemo(() => {
    const now = new Date();
    return resolveRotationAnchorUtc(now, window, config.rules || [], timezone);
  }, [window.windowId, window.startDayOfWeek, window.startTimeLocal, window.durationUnit, window.durationValue, timezone, config.rules]);
  const projectionAnchor = projectionAnchorInfo.anchorUtc;
  const lockTargetRotationId = useMemo(() => tryParseLockTargetRotationId(config.rotation.lockKey), [config.rotation.lockKey]);

  const projection = useMemo(() => {
    if (!showProjection || orderedUserIds.length === 0 || durationHours <= 0) return [] as Array<{ idx: number; start: Date; end: Date; userLabel: string }>;
    const userMap = new Map(users.map(u => [u.userId, u]));
    const rows: Array<{ idx: number; start: Date; end: Date; userLabel: string }> = [];
    const effectiveOrder = orderedUserIds.filter(uid => activeUsers.some(u => u.userId === uid));
    if (effectiveOrder.length === 0) return rows;
    let cursor = projectionAnchor;
    for (let i = 0; i < 52; i++) {
      const uid = effectiveOrder[i % effectiveOrder.length];
      const next = advanceByDurationDstAware(cursor, durationHours, timezone);
      const u = userMap.get(uid);
      rows.push({ idx: i + 1, start: cursor, end: next, userLabel: u?.displayName || u?.email || uid });
      cursor = next;
    }
    return rows;
  }, [showProjection, orderedUserIds, durationHours, users, activeUsers, projectionAnchor, timezone]);

  return (
    <div className="modal-overlay" {...closeGuard.backdropProps}>
      <div className="modal modal-roster" onClick={(e) => e.stopPropagation()}>
        <h3>Reorder Roster: {config.rotation.name}</h3>
        <p className="help-text">Drag and drop users or use arrows to reorder. Use 📅 to remove a member from an effective date with a preview. Use 🛟 to cover a member's whole rotation blocks with a replacement.</p>
        {attention.size > 0 && (
          <div className="roster-attention" role="alert" style={{ border: '1px solid rgba(234,179,8,0.5)', background: 'rgba(234,179,8,0.08)', borderRadius: 8, padding: 8, marginBottom: 8 }}>
            <strong>Needs attention</strong>
            {config.roster.filter(r => attention.has(r.userId)).map(r => (
              <div key={r.userId} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span>
                  <span className="badge badge-warning">⚠ {attention.get(r.userId) === 'inactive' ? 'Inactive' : 'Not in group'}</span>{' '}
                  {memberLabel(r.userId)}
                </span>
                {removeButton(r.userId, 'Remove from roster', 'btn-secondary')}
              </div>
            ))}
          </div>
        )}
        {config.roster.length === 0 && (
          <div className="error-banner roster-empty-banner" role="alert" style={{ marginBottom: 8 }}>No members – coverage gap until members are added.</div>
        )}
        {lockTargetRotationId && (
          <div className="help-text" style={{ marginBottom: 8 }}>
            This rotation is lock-linked to another rotation. Use <strong>Redeploy from user/date</strong> to realign future roster windows.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <h4 style={{ margin: '0 0 0.5rem' }}>Current Order (Active users only)</h4>
            <div style={{ border: '1px solid var(--border-color,#ddd)', borderRadius: 8, padding: 8, minHeight: 220 }}>
              {orderedUserIds
                .filter(uid => activeUsers.some(u => u.userId === uid))
                .map((uid, idx) => {
                const u = userMap.get(uid);
                return (
                  <div
                    key={uid}
                    draggable
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, idx)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid var(--border-color,#e2e8f0)',
                      borderRadius: 6,
                      padding: '0.4rem 0.5rem',
                      marginBottom: 6,
                      background: 'var(--card-bg)',
                    }}
                  >
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ opacity: 0.65, cursor: 'grab' }}>⋮⋮</span>
                      <strong>#{idx + 1}</strong>
                      <span>{u?.displayName || u?.email || uid}</span>
                    </span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      <button className="btn-icon" title="Move up" onClick={() => move(idx, idx - 1)}>↑</button>
                      <button className="btn-icon" title="Move down" onClick={() => move(idx, idx + 1)}>↓</button>
                      {savedRosterIds.has(uid) && removeButton(uid, '📅', 'btn-icon')}
                      {savedRosterIds.has(uid) && onRequestCover && (
                        <button
                          type="button"
                          className="btn-icon"
                          disabled={!canRemoveMembers}
                          title={canRemoveMembers ? 'Cover rotation: replace this operator for whole blocks (illness/leave)' : 'Only schedule managers or team leads can add covers'}
                          aria-label={`Cover ${memberLabel(uid)}`}
                          onClick={() => onRequestCover(uid, memberLabel(uid))}
                        >🛟</button>
                      )}
                      <button
                        className="btn-icon"
                        title="Remove from this order (applies when saved)"
                        onClick={() => setOrderedUserIds(prev => prev.filter(x => x !== uid))}
                      >✕</button>
                    </span>
                  </div>
                );
              })}
              {orderedUserIds.length === 0 && <div className="empty-row">No roster members selected.</div>}
            </div>
          </div>

          <div>
            <h4 style={{ margin: '0 0 0.5rem' }}>Add Active Members</h4>
            <div style={{ border: '1px solid var(--border-color,#ddd)', borderRadius: 8, padding: 8, maxHeight: 260, overflowY: 'auto', background: 'var(--card-bg)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                  className="btn-secondary"
                  style={{ padding: '0.2rem 0.5rem' }}
                  disabled={availableToAdd.length === 0}
                  onClick={() => setOrderedUserIds(prev => [...prev, ...availableToAdd.map(u => u.userId)])}
                >Add All</button>
              </div>
              {availableToAdd.map(u => (
                <div key={u.userId} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span>{u.displayName || u.email}</span>
                  <button className="btn-secondary" style={{ padding: '0.2rem 0.5rem' }} onClick={() => setOrderedUserIds(prev => [...prev, u.userId])}>Add</button>
                </div>
              ))}
              {availableToAdd.length === 0 && <div className="empty-row">All users already in roster.</div>}
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>Inactive users are excluded from roster administration.</div>
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => setShowProjection(prev => !prev)}>{showProjection ? 'Hide Preview' : 'Show Allocation Preview'}</button>
          <button className="btn-secondary" disabled={saving} onClick={() => void closeGuard.requestClose()}>Cancel</button>
          <button
            className="btn-primary"
            disabled={orderedUserIds.length === 0 || saving}
            onClick={async () => {
              setSaving(true);
              setSaveError(null);
              try {
                const activeOrdered = orderedUserIds.filter(uid => activeUsers.some(u => u.userId === uid));
                await tenantApi.putScheduleRoster(scheduleId, config.rotation.rotationId, activeOrdered);
                globalThis.window?.dispatchEvent(new CustomEvent('nog:schedules:changed'));
                await onSaved();
              } catch (error) {
                setSaveError(error instanceof Error ? error.message : 'Failed to save roster');
              } finally {
                setSaving(false);
              }
            }}
          >Save Order</button>
          {saveError && <div className="error-banner" role="alert">{saveError}</div>}
          <button
            className="btn-secondary"
            type="button"
            disabled={orderedUserIds.length === 0}
            onClick={async () => {
              const activeOrdered = orderedUserIds.filter(uid => activeUsers.some(u => u.userId === uid));
              if (activeOrdered.length === 0) {
                alert('No active users are available to redeploy from.');
                return;
              }

              const selected = prompt(
                'Redeploy future roster from which user? Enter #index from Current Order (for example #2), or exact display name/email.',
                '#1'
              );

              if (!selected) return;

              let chosenUserId: string | null = null;
              const trimmed = selected.trim();

              if (/^#?\d+$/.test(trimmed)) {
                const idx = Number(trimmed.replace('#', '')) - 1;
                if (idx >= 0 && idx < activeOrdered.length) {
                  chosenUserId = activeOrdered[idx];
                }
              }

              if (!chosenUserId) {
                const normalized = trimmed.toLowerCase();
                chosenUserId = activeOrdered.find((uid) => {
                  const u = userMap.get(uid);
                  const display = (u?.displayName || '').toLowerCase();
                  const email = (u?.email || '').toLowerCase();
                  return display === normalized || email === normalized;
                }) || null;
              }

              if (!chosenUserId) {
                alert('Could not match that user. Use #index from Current Order (for example #2).');
                return;
              }

              const effectiveFromUtc = new Date(Date.now() + 60 * 1000).toISOString();
              await tenantApi.redeployScheduleRoster(
                scheduleId,
                config.rotation.rotationId,
                chosenUserId,
                effectiveFromUtc,
                lockTargetRotationId ? 'Roster redeploy and lock realignment' : 'Roster redeploy'
              );
              globalThis.window?.dispatchEvent(new CustomEvent('nog:schedules:changed'));
              await onSaved();
            }}
          >Redeploy from user/date</button>
        </div>

        {showProjection && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: '0 0 0.5rem' }}>Projected Allocations (Looping Roster)</h4>
            <div className="help-text" style={{ marginBottom: 8 }}>
              Anchor: {formatDateTime(projectionAnchor, { timeZone: timezone })} ({timezone})
              {' | '}
              Source: {projectionAnchorInfo.source === 'rule' ? 'rule-start' : 'window-start (fallback)'}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {projection.map(r => (
                  <tr key={r.idx}>
                    <td>{r.idx}</td>
                    <td>{formatDateTime(r.start, { timeZone: timezone })}</td>
                    <td>{formatDateTime(r.end, { timeZone: timezone })}</td>
                    <td>{r.userLabel}</td>
                  </tr>
                ))}
                {projection.length === 0 && <tr><td colSpan={4} className="empty-row">No projection available.</td></tr>}
              </tbody>
            </table>

            </div>
            )}

          </div>
      {closeGuard.dialog}
    </div>
  );
};

const CreateScheduleModal: React.FC<{ schedules: Schedule[]; onClose: () => void; onCreated: () => Promise<void>; }> = ({ schedules, onClose, onCreated }) => {
  const [name, setName] = useState('Primary On-Call');
  const [timezone, setTimezone] = useState('UTC');
  const [scheduleType, setScheduleType] = useState<'Primary' | 'Escalation'>('Primary');
  const [startDayOfWeek, setStartDayOfWeek] = useState(1);
  const [startTimeLocal, setStartTimeLocal] = useState('09:00');
  const [durationUnit, setDurationUnit] = useState<'Hours' | 'Days'>('Hours');
  const [durationValue, setDurationValue] = useState(168);
  const [linkedPrimaryScheduleId, setLinkedPrimaryScheduleId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>Create Schedule</h3>
        <div className="form-group">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Timezone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Schedule Type</label>
            <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value as 'Primary' | 'Escalation')}>
              {SCHEDULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Window Start Day</label><select value={startDayOfWeek} onChange={(e) => setStartDayOfWeek(Number(e.target.value))}>{DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
          <div className="form-group"><label>Window Start Time</label><input type="time" value={startTimeLocal} onChange={(e) => setStartTimeLocal(e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Window Duration Unit</label><select value={durationUnit} onChange={(e) => setDurationUnit(e.target.value as 'Hours' | 'Days')}><option value="Hours">Hours</option><option value="Days">Days</option></select></div>
          <div className="form-group"><label>Window Duration</label><input type="number" min={1} value={durationValue} onChange={(e) => setDurationValue(Number(e.target.value))} /></div>
        </div>
        {scheduleType === 'Escalation' && (
          <div className="form-group">
            <label>Lock to Primary Schedule</label>
            <select value={linkedPrimaryScheduleId} onChange={(e) => setLinkedPrimaryScheduleId(e.target.value)}>
              <option value="">None</option>
              {schedules
                .filter(s => s.scheduleType === 'Primary')
                .map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.name}</option>)}
            </select>
            <div className="help-text">Locks initial window settings to a selected primary schedule for easier paired setup.</div>
          </div>
        )}
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={submitting} onClick={async () => {
            setSubmitting(true);
            let effectiveStartDayOfWeek = startDayOfWeek;
            let effectiveStartTimeLocal = startTimeLocal;
            let effectiveDurationUnit = durationUnit;
            let effectiveDurationValue = durationValue;

            if (scheduleType === 'Escalation' && linkedPrimaryScheduleId) {
              try {
                const primaryWindows = await tenantApi.getScheduleWindows(linkedPrimaryScheduleId);
                const primaryWindow = (primaryWindows || []).slice().sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))[0];
                if (primaryWindow) {
                  effectiveStartDayOfWeek = primaryWindow.startDayOfWeek;
                  effectiveStartTimeLocal = primaryWindow.startTimeLocal;
                  effectiveDurationUnit = (primaryWindow.durationUnit as 'Hours' | 'Days') || effectiveDurationUnit;
                  effectiveDurationValue = primaryWindow.durationValue || effectiveDurationValue;
                }
              } catch {
                // Fall back to manual values if primary schedule windows cannot be read.
              }
            }

            const created = await tenantApi.createSchedule({
              name,
              timezone,
              rotationType: 'Weekly',
              scheduleType,
              linkedPrimaryScheduleId: scheduleType === 'Escalation' ? (linkedPrimaryScheduleId || null) : null,
              startDate: new Date().toISOString(),
              memberIds: [],
            });
            const createdWindow = await tenantApi.createScheduleWindow(created.scheduleId, {
              name: 'Primary Window',
              startDayOfWeek: effectiveStartDayOfWeek,
              startTimeLocal: effectiveStartTimeLocal,
              durationUnit: effectiveDurationUnit,
              durationValue: effectiveDurationValue,
              sequenceOrder: 0,
              isActive: true,
            });
            await tenantApi.updateSchedule(created.scheduleId, { primaryWindowId: createdWindow.windowId });
            await onCreated();
          }}>{submitting ? 'Creating...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
};

const EditScheduleModal: React.FC<{ schedule: Schedule; schedules: Schedule[]; onClose: () => void; onSaved: () => Promise<void>; }> = ({ schedule, schedules, onClose, onSaved }) => {
  const [name, setName] = useState(schedule.name || '');
  const [timezone, setTimezone] = useState(schedule.timezone || 'UTC');
  const [scheduleType, setScheduleType] = useState<'Primary' | 'Escalation'>((schedule.scheduleType as 'Primary' | 'Escalation') || 'Primary');
  const [primaryWindowId, setPrimaryWindowId] = useState(schedule.primaryWindowId || '');
  const [linkedPrimaryScheduleId, setLinkedPrimaryScheduleId] = useState(schedule.linkedPrimaryScheduleId || '');
  const [active, setActive] = useState(schedule.isActive);
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);
  const [startDayOfWeek, setStartDayOfWeek] = useState(1);
  const [startTimeLocal, setStartTimeLocal] = useState('09:00');
  const [durationUnit, setDurationUnit] = useState<'Hours' | 'Days'>('Hours');
  const [durationValue, setDurationValue] = useState(168);

  useEffect(() => {
    const load = async () => {
      const ws = await tenantApi.getScheduleWindows(schedule.scheduleId);
      setWindows(ws || []);
      if (!schedule.primaryWindowId && ws && ws.length > 0) {
        setPrimaryWindowId(ws[0].windowId);
      }
      if (ws && ws.length > 0) {
        setStartDayOfWeek(ws[0].startDayOfWeek);
        setStartTimeLocal(ws[0].startTimeLocal);
        setDurationUnit((ws[0].durationUnit as 'Hours' | 'Days') || 'Hours');
        setDurationValue(ws[0].durationValue || 168);
      }
    };
    load();
  }, [schedule.scheduleId]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Schedule</h3>
        <div className="form-group">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Timezone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Schedule Type</label>
            <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value as 'Primary' | 'Escalation')}>
              {SCHEDULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        {scheduleType === 'Escalation' && (
          <div className="form-group">
            <label>Linked Primary Schedule</label>
            <select value={linkedPrimaryScheduleId} onChange={(e) => setLinkedPrimaryScheduleId(e.target.value)}>
              <option value="">None</option>
              {schedules
                .filter(s => s.scheduleType === 'Primary' && s.scheduleId !== schedule.scheduleId)
                .map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.name}</option>)}
            </select>
            <div className="help-text">Persists explicit primary/escalation linkage for timeline pairing.</div>
          </div>
        )}
        <div className="form-group">
          <label>Primary Window</label>
          <select
            value={primaryWindowId}
            onChange={(e) => setPrimaryWindowId(e.target.value)}
            disabled={windows.length === 0}
          >
            <option value="">None</option>
            {windows.map(w => (
              <option key={w.windowId} value={w.windowId}>{w.name}</option>
            ))}
          </select>
          <div className="help-text">Used by scheduler as the primary anchoring window.</div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Window Start Day</label><select value={startDayOfWeek} onChange={(e) => setStartDayOfWeek(Number(e.target.value))}>{DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
          <div className="form-group"><label>Window Start Time</label><input type="time" value={startTimeLocal} onChange={(e) => setStartTimeLocal(e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Window Duration Unit</label><select value={durationUnit} onChange={(e) => setDurationUnit(e.target.value as 'Hours' | 'Days')}><option value="Hours">Hours</option><option value="Days">Days</option></select></div>
          <div className="form-group"><label>Window Duration</label><input type="number" min={1} value={durationValue} onChange={(e) => setDurationValue(Number(e.target.value))} /></div>
        </div>
        <div className="form-group">
          <label>Active</label>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        </div>
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={async () => {
            await tenantApi.updateSchedule(schedule.scheduleId, {
              name,
              timezone,
              scheduleType,
              primaryWindowId: primaryWindowId || null,
              linkedPrimaryScheduleId: scheduleType === 'Escalation' ? (linkedPrimaryScheduleId || null) : null,
              isActive: active,
            });
            if (windows.length > 0) {
              await tenantApi.updateScheduleWindow(schedule.scheduleId, windows[0].windowId, {
                name: windows[0].name,
                startDayOfWeek,
                startTimeLocal,
                durationUnit,
                durationValue,
                sequenceOrder: windows[0].sequenceOrder,
                isActive: windows[0].isActive,
              });
            }
            await onSaved();
          }}>Save</button>
        </div>
      </div>
    </div>
  );
};

const WindowModal: React.FC<{
  scheduleId: string;
  window?: ScheduleWindow;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onDeleted: () => Promise<void>;
  confirmDelete: () => Promise<boolean>;
}> = ({ scheduleId, window, onClose, onSaved, onDeleted, confirmDelete }) => {
  const [name, setName] = useState(window?.name || 'Standard Week');
  const [startDayOfWeek, setStartDayOfWeek] = useState(window?.startDayOfWeek ?? 1);
  const [startTimeLocal, setStartTimeLocal] = useState(window?.startTimeLocal || '09:00');
  const [durationUnit, setDurationUnit] = useState(window?.durationUnit || 'Hours');
  const [durationValue, setDurationValue] = useState(window?.durationValue ?? 168);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>{window ? 'Edit Window' : 'Create Window'}</h3>
        <div className="form-group"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="form-row">
          <div className="form-group"><label>Start Day</label><select value={startDayOfWeek} onChange={(e) => setStartDayOfWeek(Number(e.target.value))}>{DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
          <div className="form-group"><label>Start Time</label><input type="time" value={startTimeLocal} onChange={(e) => setStartTimeLocal(e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Timezone</label><div className="help-text">Uses schedule timezone from Schedule settings.</div></div>
        <div className="form-row">
          <div className="form-group"><label>Duration Unit</label><select value={durationUnit} onChange={(e) => setDurationUnit(e.target.value)}><option value="Hours">Hours</option><option value="Days">Days</option></select></div>
          <div className="form-group"><label>Duration Value</label><input type="number" value={durationValue} onChange={(e) => setDurationValue(Number(e.target.value))} /></div>
        </div>
        {error && <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div>}
        <div className="form-actions">
          {window && (
            <button
              className="btn-danger"
              onClick={async () => {
                if (!await confirmDelete()) return;
                setError(null);
                try {
                  await tenantApi.deleteScheduleWindow(scheduleId, window.windowId);
                  await onDeleted();
                } catch (err: any) {
                  setError(err?.message || 'Failed to delete window');
                }
              }}
            >Delete Window</button>
          )}
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving} onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              const payload = { name, startDayOfWeek, startTimeLocal, durationUnit, durationValue, sequenceOrder: 0, isActive: true };
              if (window) await tenantApi.updateScheduleWindow(scheduleId, window.windowId, payload);
              else await tenantApi.createScheduleWindow(scheduleId, payload);
              await onSaved();
            } catch (err: any) {
              setError(err?.message || 'Failed to save window');
            } finally {
              setSaving(false);
            }
          }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const RotationModal: React.FC<{
  scheduleId: string;
  windowId: string;
  windowDurationMinutes: number;
  groups: Group[];
  lockTargets?: Array<{ rotationId: string; name: string }>;
  existing?: RotationConfig;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ scheduleId, windowId, windowDurationMinutes, groups, lockTargets = [], existing, onClose, onSaved }) => {
  useDateTimePreferences();
  const [name, setName] = useState(existing?.rotation.name || 'Business Hours');
  const [groupId, setGroupId] = useState(existing?.rotation.groupId || groups[0]?.groupId || '');
  const [assignmentDurationHours, setAssignmentDurationHours] = useState((existing?.rotation.assignmentDurationMinutes || 168 * 60) / 60);
  const [lockKey, setLockKey] = useState(existing?.rotation.lockKey || '');
  const [rules, setRules] = useState<RotationRuleEditor[]>(
    (existing?.rules || [{ dayOfWeek: 1, startTimeLocal: '09:00', endTimeLocal: '17:00', isOvernight: false }])
      .map(r => {
        const totalMinutes = resolveRuleDurationMinutes(r, windowDurationMinutes);
        const safeMinutes = Math.max(1, totalMinutes);
        return {
          dayOfWeek: r.dayOfWeek,
          startTimeLocal: r.startTimeLocal,
          endTimeLocal: r.endTimeLocal,
          isOvernight: !!r.isOvernight,
          durationHours: Math.floor(safeMinutes / 60),
          durationMinutes: safeMinutes % 60,
        };
      })
  );
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [draftRule, setDraftRule] = useState<{
    dayOfWeek: number;
    startTimeLocal: string;
    endTimeLocal: string;
    isOvernight: boolean;
    durationHours: number;
    durationMinutes: number;
  }>({
    dayOfWeek: 1,
    startTimeLocal: '09:00',
    endTimeLocal: '17:00',
    isOvernight: false,
    durationHours: 8,
    durationMinutes: 0,
  });

  const initialDraft = useRef(JSON.stringify({ name, groupId, assignmentDurationHours, lockKey, rules }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const closeGuard = useScheduleEditorCloseGuard(
    JSON.stringify({ name, groupId, assignmentDurationHours, lockKey, rules }) !== initialDraft.current || editingRuleIndex !== null,
    saving, onClose,
  );
  const applyDurationToDraft = () => {
    const start = buildDateForDowAndTime(draftRule.dayOfWeek, draftRule.startTimeLocal);
    const hours = Math.max(0, Number(draftRule.durationHours || 0));
    const minutes = Math.max(0, Number(draftRule.durationMinutes || 0));
    const end = new Date(start.getTime() + (hours * 60 + minutes) * 60000);
    const endTimeLocal = `${String(end.getUTCHours()).padStart(2, '0')}:${String(end.getUTCMinutes()).padStart(2, '0')}`;
    setDraftRule(prev => ({ ...prev, endTimeLocal, isOvernight: (hours * 60 + minutes) >= 1440 || end.getUTCDay() !== start.getUTCDay() }));
  };

  return (
    <div className="modal-overlay" {...closeGuard.backdropProps}>
      <div className="modal modal-rotation modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>{existing ? 'Edit Rotation' : 'Create Rotation'}</h3>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-group"><label>Group</label><select value={groupId} onChange={(e) => setGroupId(e.target.value)}>{groups.map(g => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}</select></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Assignment Duration (hours)</label><input type="number" min={0.5} step={0.5} value={assignmentDurationHours} onChange={(e) => setAssignmentDurationHours(Number(e.target.value))} /></div>
          <div className="form-group">
            <label>Lock To Rotation (optional)</label>
            <select value={lockKey} onChange={(e) => setLockKey(e.target.value)}>
              <option value="">No lock</option>
              {lockTargets
                .filter(t => !existing || t.rotationId !== existing.rotation.rotationId)
                .map(t => <option key={t.rotationId} value={`rotation:${t.rotationId}`}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Coverage Rules</label>
          <table className="data-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Start</th>
                <th>End</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, idx) => (
                <tr key={idx}>
                  <td>{DOW[r.dayOfWeek]}</td>
                  <td>{formatWallTime(r.startTimeLocal)}</td>
                  <td>{formatWallTime(r.endTimeLocal)}</td>
                  <td>{`${r.durationHours ?? 0}h ${r.durationMinutes ?? 0}m`}</td>
                  <td>
                    <div className="rotation-rule-actions">
                    <button
                      className="btn-icon"
                      title="Edit rule"
                      onClick={() => {
                        setEditingRuleIndex(idx);
                        setDraftRule({
                          ...r,
                          durationHours: r.durationHours ?? 0,
                          durationMinutes: r.durationMinutes ?? 0,
                        });
                      }}
                    >✏️</button>
                    <button
                      className="btn-icon"
                      title="Duplicate rule"
                      onClick={() => {
                        setEditingRuleIndex(-1);
                        setDraftRule({ ...r, dayOfWeek: r.dayOfWeek, startTimeLocal: r.startTimeLocal, endTimeLocal: r.endTimeLocal, isOvernight: !!r.isOvernight, durationHours: r.durationHours ?? 0, durationMinutes: r.durationMinutes ?? 0 });
                      }}
                    >📄</button>
                    <button className="btn-icon" title="Delete rule" onClick={() => setRules(prev => prev.filter((_, i) => i !== idx))}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && <tr><td colSpan={5} className="empty-row">No rules configured</td></tr>}
            </tbody>
          </table>

          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                setEditingRuleIndex(-1);
                setDraftRule({ dayOfWeek: 1, startTimeLocal: '09:00', endTimeLocal: '17:00', isOvernight: false, durationHours: 8, durationMinutes: 0 });
              }}
            >+ Add Rule</button>
          </div>

          {editingRuleIndex !== null && (
            <div style={{ marginTop: 12, border: '1px solid var(--border-color,#e2e8f0)', borderRadius: 8, padding: 10 }}>
              <div className="form-row" style={{ alignItems: 'end' }}>
                <div className="form-group"><label>Day</label><select value={draftRule.dayOfWeek} onChange={(e) => setDraftRule(prev => ({ ...prev, dayOfWeek: Number(e.target.value) }))}>{DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
                <div className="form-group"><label>Start</label><input type="time" value={draftRule.startTimeLocal} onChange={(e) => setDraftRule(prev => ({ ...prev, startTimeLocal: e.target.value }))} /></div>
                <div className="form-group"><label>Duration (h)</label><input type="number" min={0} value={draftRule.durationHours} onChange={(e) => setDraftRule(prev => ({ ...prev, durationHours: Number(e.target.value) }))} /></div>
                <div className="form-group"><label>Duration (m)</label><input type="number" min={0} max={59} value={draftRule.durationMinutes} onChange={(e) => setDraftRule(prev => ({ ...prev, durationMinutes: Number(e.target.value) }))} /></div>
                <button className="btn-secondary" style={{ padding: '0.25rem 0.45rem', height: 32 }} type="button" onClick={applyDurationToDraft}>Calc End</button>
                <div className="form-group"><label>End</label><input type="text" value={draftRule.endTimeLocal} readOnly /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={() => {
                    const withCalc = { ...draftRule };
                    const start = buildDateForDowAndTime(withCalc.dayOfWeek, withCalc.startTimeLocal);
                    const end = new Date(start.getTime() + ((withCalc.durationHours || 0) * 60 + (withCalc.durationMinutes || 0)) * 60000);
                    withCalc.endTimeLocal = `${String(end.getUTCHours()).padStart(2, '0')}:${String(end.getUTCMinutes()).padStart(2, '0')}`;
                    withCalc.isOvernight = ((withCalc.durationHours || 0) * 60 + (withCalc.durationMinutes || 0)) >= 1440 || end.getUTCDay() !== start.getUTCDay();
                    if (editingRuleIndex === -1) {
                      setRules(prev => [...prev, withCalc]);
                    } else {
                      setRules(prev => prev.map((x, i) => i === editingRuleIndex ? withCalc : x));
                    }
                    setEditingRuleIndex(null);
                  }}
                >Save Rule</button>
                {editingRuleIndex !== -1 && (
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => {
                      setRules(prev => [...prev, draftRule]);
                    }}
                  >Duplicate</button>
                )}
                <button className="btn-secondary" type="button" onClick={() => setEditingRuleIndex(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

        <div className="help-text" style={{ marginBottom: 10 }}>
          Roster membership and future allocations are managed in <strong>Roster Admin</strong> from the rotation table.
        </div>
        <div className="form-actions">
          <button className="btn-secondary" disabled={saving} onClick={() => void closeGuard.requestClose()}>Cancel</button>
          <button className="btn-primary" disabled={saving} onClick={async () => {
            setSaving(true);
            setSaveError(null);
            try {
              const payload = {
                name,
                groupId,
                assignmentDurationMinutes: Math.max(1, Math.round(assignmentDurationHours * 60)),
                priority: 0,
                lockKey: lockKey || null,
                isActive: true,
                rules: rules.map(toRulePayload),
              };
              if (existing) {
                await tenantApi.updateScheduleRotation(scheduleId, windowId, existing.rotation.rotationId, payload);
              } else {
                await tenantApi.createScheduleRotation(scheduleId, windowId, payload);
              }
              await onSaved();
            } catch (error) {
              setSaveError(error instanceof Error ? error.message : 'Failed to save rotation');
            } finally {
              setSaving(false);
            }
          }}>Save</button>
        </div>
        {saveError && <div className="error-banner" role="alert">{saveError}</div>}
      </div>
      {closeGuard.dialog}
    </div>
  );
};

const SwapModal: React.FC<{
  scheduleId: string;
  scheduleTimezone: string;
  users: User[];
  windows: ScheduleWindow[];
  source?: TimelineSwapSelection | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ scheduleId, scheduleTimezone, users, windows, source, onClose, onSaved }) => {
  useDateTimePreferences();
  const timezone = scheduleTimezone || 'UTC';
  const sourceEntry = source?.entry;
  const sourceGroupId = source?.rotationGroupId || null;
  const sourceGroupName = source?.rotationGroupName || 'the source rotation group';
  const [userAId, setUserAId] = useState(sourceEntry?.userId || users[0]?.userId || '');
  const [userBId, setUserBId] = useState(
    users.find((u) => u.userId !== (sourceEntry?.userId || ''))?.userId
    || users[1]?.userId
    || users[0]?.userId
    || ''
  );
  const [rotationId, setRotationId] = useState('');
  const [startUtc, setStartUtc] = useState(
    sourceEntry ? toDateTimeLocalInTimezone(sourceEntry.startUtc, timezone) : toDateTimeLocalInTimezone(new Date(Date.now() + 5 * 60 * 1000).toISOString(), timezone)
  );
  const [endUtc, setEndUtc] = useState(
    sourceEntry ? toDateTimeLocalInTimezone(sourceEntry.endUtc, timezone) : toDateTimeLocalInTimezone(new Date(Date.now() + (8 * 3600000) + (5 * 60 * 1000)).toISOString(), timezone)
  );
  const [reason, setReason] = useState('One-time swap');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [eligibleGroupMembers, setEligibleGroupMembers] = useState<GroupMemberUser[]>([]);
  const [loadingEligibleGroupMembers, setLoadingEligibleGroupMembers] = useState(false);
  const [eligibleGroupError, setEligibleGroupError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadEligibleGroupMembers = async () => {
      if (!sourceGroupId) {
        setEligibleGroupMembers([]);
        setEligibleGroupError(null);
        setLoadingEligibleGroupMembers(false);
        return;
      }

      setLoadingEligibleGroupMembers(true);
      setEligibleGroupError(null);

      try {
        const members = await tenantApi.getGroupMembers(sourceGroupId);
        if (!cancelled) {
          setEligibleGroupMembers(members || []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setEligibleGroupMembers([]);
          setEligibleGroupError(err?.message || 'Failed to load eligible users.');
        }
      } finally {
        if (!cancelled) {
          setLoadingEligibleGroupMembers(false);
        }
      }
    };

    void loadEligibleGroupMembers();

    return () => {
      cancelled = true;
    };
  }, [sourceGroupId]);

  const eligibleUserIds = useMemo(() => new Set(eligibleGroupMembers.map((member) => member.userId)), [eligibleGroupMembers]);
  const eligibleUsers = useMemo(() => {
    if (!sourceGroupId) {
      return users;
    }

    return users.filter((user) => eligibleUserIds.has(user.userId));
  }, [eligibleUserIds, sourceGroupId, users]);
  const userBOptions = useMemo(
    () => eligibleUsers.filter((user) => user.userId !== userAId),
    [eligibleUsers, userAId]
  );

  useEffect(() => {
    if (sourceGroupId && !eligibleUsers.some((user) => user.userId === userAId)) {
      setUserAId(eligibleUsers[0]?.userId || sourceEntry?.userId || '');
    }
  }, [eligibleUsers, sourceEntry?.userId, sourceGroupId, userAId]);

  useEffect(() => {
    if (sourceGroupId && !userBOptions.some((user) => user.userId === userBId)) {
      setUserBId(userBOptions[0]?.userId || '');
    }
  }, [sourceGroupId, userBId, userBOptions]);

  const eligibilityHint = sourceGroupId
    ? `Eligible users are limited to ${sourceGroupName}. The selected source user must still have projected coverage in the interval.`
    : 'Select a source shift to scope swap eligibility to a rotation group.';

  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="modal modal-scroll" role="dialog" aria-modal="true" aria-labelledby="create-swap-title"
        aria-busy={submitting} onClick={(e) => e.stopPropagation()}>
        <h3 id="create-swap-title">Create Swap</h3>
        {sourceEntry && (
          <div className="help-text" style={{ marginBottom: 8 }}>
            Source shift: {source?.windowName || 'Timeline'} | {sourceEntry.rotationName} | {sourceEntry.userName} | {formatDateTimeLabelInTimezone(sourceEntry.startUtc, timezone)} {'->'} {formatDateTimeLabelInTimezone(sourceEntry.endUtc, timezone)}
          </div>
        )}
        <div className="form-group"><label>User A</label><select value={userAId} onChange={(e) => setUserAId(e.target.value)}>{(sourceGroupId ? eligibleUsers : users).map(u => <option key={u.userId} value={u.userId}>{u.displayName || u.email}</option>)}</select></div>
        <div className="form-group"><label>User B</label><select value={userBId} onChange={(e) => setUserBId(e.target.value)}>{userBOptions.map(u => <option key={u.userId} value={u.userId}>{u.displayName || u.email}</option>)}</select></div>
        <div className="form-group"><label>Rotation ID (optional)</label><input value={rotationId} onChange={(e) => setRotationId(e.target.value)} placeholder="Paste rotation id only if you want to scope the swap" /></div>
        <div className="help-text" style={{ marginBottom: 8 }}>
          {loadingEligibleGroupMembers ? 'Loading eligible users for this rotation group...' : eligibilityHint}
        </div>
        {eligibleGroupError && (
          <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{eligibleGroupError}</div>
        )}
        <div className="form-row">
          <div className="form-group"><label>Start ({timezone})</label><input type="datetime-local" value={startUtc} onChange={(e) => setStartUtc(e.target.value)} /></div>
          <div className="form-group"><label>End ({timezone})</label><input type="datetime-local" value={endUtc} onChange={(e) => setEndUtc(e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Reason</label><input value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        {submitError && <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{submitError}</div>}
        <div className="form-actions">
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn-primary" disabled={submitting || (Boolean(sourceGroupId) && (loadingEligibleGroupMembers || eligibleUsers.length === 0))} onClick={async () => {
            setSubmitError(null);
            try {
              if (!userAId || !userBId) {
                setSubmitError('Please choose both users.');
                return;
              }
              if (userAId === userBId) {
                setSubmitError('User A and User B must be different users.');
                return;
              }
              if (sourceGroupId && !eligibleUsers.some((user) => user.userId === userAId)) {
                setSubmitError(`User A must be a member of ${sourceGroupName}.`);
                return;
              }
              if (sourceGroupId && !eligibleUsers.some((user) => user.userId === userBId)) {
                setSubmitError(`User B must be a member of ${sourceGroupName}.`);
                return;
              }

              const start = parseDateTimeLocalInTimezone(startUtc, timezone);
              const end = parseDateTimeLocalInTimezone(endUtc, timezone);
              const now = new Date();
              if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                setSubmitError(`Please enter a valid start/end time in ${timezone}.`);
                return;
              }
              if (start <= now) {
                setSubmitError('Swap start must be in the future.');
                return;
              }
              if (end <= start) {
                setSubmitError('Swap end must be after start.');
                return;
              }
              if (end.getTime() > now.getTime() + (8 * 7 * 24 * 60 * 60 * 1000)) {
                setSubmitError('Swap interval must be within the next 8 weeks.');
                return;
              }

              setSubmitting(true);
              await tenantApi.createScheduleSwap(scheduleId, {
                rotationId: rotationId || undefined,
                userAId,
                userBId,
                startUtc: start.toISOString(),
                endUtc: end.toISOString(),
                reason,
                isOneTime: true,
              });
              await onSaved();
            } catch (err: any) {
              setSubmitError(err?.message || 'Failed to create swap.');
            } finally {
              setSubmitting(false);
            }
          }}>{submitting ? 'Creating...' : 'Create'}</button>
        </div>
        {windows.length > 0 && <div className="help-text">Tip: swap time should align with schedule windows.</div>}
      </div>
    </div>
  );
};

function buildDateForDowAndTime(dayOfWeek: number, time: string): Date {
  const now = new Date();
  const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const delta = (dayOfWeek - base.getUTCDay() + 7) % 7;
  base.setUTCDate(base.getUTCDate() + delta);
  const [h, m] = time.split(':').map(Number);
  base.setUTCHours(h || 0, m || 0, 0, 0);
  return base;
}

function toTimelineEntryKey(entry: { rotationId: string; startUtc: string; endUtc: string; userId: string }): string {
  return `${entry.rotationId}|${entry.startUtc}|${entry.endUtc}|${entry.userId}`;
}


function formatDateTimeLabelInTimezone(value: string, timezone: string): string {
  const d = roundUpToMinute(new Date(value));
  if (Number.isNaN(d.getTime())) return value;
  const tz = timezone || 'UTC';
  return `${formatDateTimeInTimezone(d, tz)} ${tz}`;
}



function toRulePayload(rule: RotationRuleEditor) {
  const durationMinutes = Math.max(1, Math.round((rule.durationHours || 0) * 60 + (rule.durationMinutes || 0)));
  return {
    dayOfWeek: rule.dayOfWeek,
    startTimeLocal: rule.startTimeLocal,
    endTimeLocal: rule.endTimeLocal,
    isOvernight: rule.isOvernight,
    durationMinutes,
  };
}

function resolveWindowDurationMinutes(window?: ScheduleWindow | null): number {
  if (!window) return 7 * 24 * 60;

  const value = Math.max(1, Number(window.durationValue || 1));
  if ((window.durationUnit || '').toLowerCase() === 'days') return value * 24 * 60;
  return value * 60;
}

function resolveRuleDurationMinutes(
  rule: { startTimeLocal: string; endTimeLocal: string; isOvernight: boolean; durationMinutes?: number },
  windowDurationMinutes: number
): number {
  if (typeof rule.durationMinutes === 'number' && rule.durationMinutes > 0) {
    return rule.durationMinutes;
  }

  const start = hhmmToMinutes(rule.startTimeLocal);
  const end = hhmmToMinutes(rule.endTimeLocal);

  if (rule.isOvernight && start === end && windowDurationMinutes > 24 * 60) {
    return windowDurationMinutes;
  }

  let duration = end - start;
  if (rule.isOvernight || duration <= 0) duration += 24 * 60;
  return Math.max(1, duration);
}

function formatRuleEndLabel(
  rule: { dayOfWeek: number; startTimeLocal: string; endTimeLocal: string; isOvernight: boolean; durationMinutes?: number },
  windowDurationMinutes: number
): string {
  const start = hhmmToMinutes(rule.startTimeLocal);
  const duration = resolveRuleDurationMinutes(rule, windowDurationMinutes);
  const dayOffset = Math.floor((start + duration) / (24 * 60));

  if (dayOffset <= 0) {
    return `${DOW[rule.dayOfWeek]} ${formatWallTime(rule.endTimeLocal)}`;
  }

  const endDay = (rule.dayOfWeek + dayOffset) % 7;
  const suffix = dayOffset > 1 ? ` (+${dayOffset}d)` : '';
  return `${DOW[endDay]} ${formatWallTime(rule.endTimeLocal)}${suffix}`;
}

function hhmmToMinutes(value: string): number {
  const [h, m] = (value || '00:00').split(':').map(Number);
  const hh = Number.isFinite(h) ? h : 0;
  const mm = Number.isFinite(m) ? m : 0;
  return hh * 60 + mm;
}

function alignToWindowAnchorUtc(referenceUtc: Date, window: ScheduleWindow, timezone: string): Date {
  const tz = timezone || 'UTC';
  const localRef = toLocalParts(referenceUtc, tz);
  const anchorTime = parseHHmm(window.startTimeLocal || '09:00');

  const localRefDate = new Date(Date.UTC(localRef.year, localRef.month - 1, localRef.day));
  const refDow = localRefDate.getUTCDay();
  const deltaDays = (refDow - (window.startDayOfWeek ?? 1) + 7) % 7;

  const anchorLocalDate = new Date(Date.UTC(localRef.year, localRef.month - 1, localRef.day - deltaDays, anchorTime.h, anchorTime.m, 0));
  let anchorUtc = localToUtc(anchorLocalDate, tz);

  const durationHours = resolveWindowDurationHours(window);
  while (advanceByDurationDstAware(anchorUtc, durationHours, tz).getTime() <= referenceUtc.getTime()) {
    anchorUtc = advanceByDurationDstAware(anchorUtc, durationHours, tz);
  }

  while (anchorUtc.getTime() > referenceUtc.getTime()) {
    anchorUtc = advanceByDurationDstAware(anchorUtc, -durationHours, tz);
  }

  return anchorUtc;
}

function resolveRotationAnchorUtc(
  referenceUtc: Date,
  window: ScheduleWindow,
  rules: Array<{ dayOfWeek: number; startTimeLocal: string }>,
  timezone: string,
): { anchorUtc: Date; source: 'rule' | 'window' } {
  const tz = timezone || 'UTC';
  const windowAnchorUtc = alignToWindowAnchorUtc(referenceUtc, window, tz);
  if (!rules || rules.length === 0) return { anchorUtc: windowAnchorUtc, source: 'window' };

  const windowDurationHours = resolveWindowDurationHours(window);
  const windowEndUtc = advanceByDurationDstAware(windowAnchorUtc, windowDurationHours, tz);
  const windowAnchorLocal = toLocalParts(windowAnchorUtc, tz);
  const windowAnchorLocalDate = new Date(Date.UTC(windowAnchorLocal.year, windowAnchorLocal.month - 1, windowAnchorLocal.day));
  const windowAnchorDow = windowAnchorLocalDate.getUTCDay();

  let best: Date | null = null;

  for (const rule of rules) {
    const t = parseHHmm(rule.startTimeLocal || '00:00');
    const deltaDays = ((rule.dayOfWeek ?? 0) - windowAnchorDow + 7) % 7;
    const candidateLocal = new Date(Date.UTC(windowAnchorLocal.year, windowAnchorLocal.month - 1, windowAnchorLocal.day + deltaDays, t.h, t.m, 0));
    let candidateUtc = localToUtc(candidateLocal, tz);

    if (candidateUtc.getTime() < windowAnchorUtc.getTime()) {
      const plus7 = new Date(Date.UTC(windowAnchorLocal.year, windowAnchorLocal.month - 1, windowAnchorLocal.day + deltaDays + 7, t.h, t.m, 0));
      candidateUtc = localToUtc(plus7, tz);
    }

    if (candidateUtc.getTime() < windowAnchorUtc.getTime() || candidateUtc.getTime() >= windowEndUtc.getTime()) continue;
    if (!best || candidateUtc.getTime() < best.getTime()) best = candidateUtc;
  }

  if (best) return { anchorUtc: best, source: 'rule' };
  return { anchorUtc: windowAnchorUtc, source: 'window' };
}

const __rosterProjectionTestApi = {
  resolveRotationAnchorUtc,
};

export { __rosterProjectionTestApi };

function resolveWindowDurationHours(window: ScheduleWindow): number {
  const v = Math.max(1, window.durationValue || 168);
  if ((window.durationUnit || '').toLowerCase() === 'days') return v * 24;
  return v;
}

/** DST-aware advance: adds durationHours in the schedule timezone so that
 *  e.g. Monday 09:00 AEST always stays Monday 09:00 AEST even across DST. */
function advanceByDurationDstAware(utcDate: Date, durationHours: number, timezone: string): Date {
  const local = toLocalParts(utcDate, timezone);
  const advanced = new Date(Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second));
  advanced.setUTCMinutes(advanced.getUTCMinutes() + durationHours * 60);
  return localToUtc(advanced, timezone);
}

function parseHHmm(value: string): { h: number; m: number } {
  const [h, m] = (value || '09:00').split(':').map(Number);
  return { h: Number.isFinite(h) ? h : 9, m: Number.isFinite(m) ? m : 0 };
}



function alignToLocalMidnightUtc(referenceUtc: Date, timezone: string): Date {
  const local = toLocalParts(referenceUtc, timezone || 'UTC');
  const localMidnightAsUtcDate = new Date(Date.UTC(local.year, local.month - 1, local.day, 0, 0, 0));
  return localToUtc(localMidnightAsUtcDate, timezone || 'UTC');
}
